import React, { useState, useEffect, useMemo, useRef, useCallback, FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Menu,
    X,
    Search,
    AlertCircle,
    CheckCircle,
    Clock,
    FileText,
    Map,
    Sparkles,
    LogOut,
    MapPin,
    Mail,
    Phone,
    User,
    BarChart3,
    MessageSquare,
    Trash2,
    Send,
    Camera,
    Link,
    Link2,
    Brain,
    LayoutDashboard,
    ChevronDown,
    ChevronLeft,
    Check,
    ExternalLink,
    AlertTriangle,
    Activity,
    History,
    Cloud,
    Shield,
    Edit3,
    Bell,
    Settings,
    Download,
    FlaskConical,
    LockKeyhole,
    Home,
    Calendar,
    RefreshCw,
    Award,
    UserCheck,
    Briefcase,
    TrendingUp,
    PlusCircle,
    ClipboardList,
    SlidersHorizontal,
    ArrowUpDown,
    Paperclip,
    GitMerge,
    Loader2,
} from 'lucide-react';
import { Button, Card, Modal, Input, Textarea, Select, StatusBadge, Badge } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { api, MapLayer } from '../services/api';
import { ServiceRequest, ServiceRequestDetail, ServiceDefinition, Statistics, AdvancedStatistics, RequestComment, ClosedSubstatus, User as UserType, Department, AuditLogEntry, HeatmapData } from '../types';
import { XAxis, YAxis, ResponsiveContainer, AreaChart, Area, Tooltip } from 'recharts';
import StaffDashboardMap from '../components/StaffDashboardMap';
import RequestDetailMap from '../components/RequestDetailMap';
import SpatialBiasHeatmap from '../components/SpatialBiasHeatmap';
import { usePageNavigation } from '../hooks/usePageNavigation';
import NotificationSettings from '../components/NotificationSettings';
import ActivityFeed from '../components/ActivityFeed';
import PrintWorkOrder from '../components/PrintWorkOrder';
import ReportsPanel from '../components/ReportsPanel';
import GoogleMapsLocationPicker from '../components/GoogleMapsLocationPicker';

type View = 'dashboard' | 'active' | 'in_progress' | 'resolved' | 'all_tickets' | 'statistics' | 'reports' | 'report' | 'deleted_tickets';

// Reusable executive-style leaderboard card for staff activity metrics
const ACCENT_STYLES: Record<string, { ring: string; text: string; bar: string; iconBg: string }> = {
    emerald: { ring: 'border-emerald-500/30', text: 'text-emerald-300', bar: 'from-emerald-400 to-emerald-600', iconBg: 'bg-emerald-500/20 text-emerald-300' },
    indigo:  { ring: 'border-indigo-500/30',  text: 'text-indigo-300',  bar: 'from-indigo-400 to-indigo-600',  iconBg: 'bg-indigo-500/20 text-indigo-300' },
    blue:    { ring: 'border-blue-500/30',    text: 'text-blue-300',    bar: 'from-blue-400 to-blue-600',    iconBg: 'bg-blue-500/20 text-blue-300' },
    amber:   { ring: 'border-amber-500/30',   text: 'text-amber-300',   bar: 'from-amber-400 to-amber-600',   iconBg: 'bg-amber-500/20 text-amber-300' },
    purple:  { ring: 'border-purple-500/30',  text: 'text-purple-300',  bar: 'from-purple-400 to-purple-600',  iconBg: 'bg-purple-500/20 text-purple-300' },
    rose:    { ring: 'border-rose-500/30',    text: 'text-rose-300',    bar: 'from-rose-400 to-rose-600',    iconBg: 'bg-rose-500/20 text-rose-300' },
};

interface StaffLeaderboardCardProps {
    title: string;
    subtitle: string;
    icon: React.ComponentType<{ className?: string }>;
    accent: 'emerald' | 'indigo' | 'blue' | 'amber' | 'purple' | 'rose';
    data: Record<string, number>;
    valueLabel?: string;
}

function StaffLeaderboardCard({ title, subtitle, icon: Icon, accent, data, valueLabel }: StaffLeaderboardCardProps) {
    const style = ACCENT_STYLES[accent];
    const entries = Object.entries(data).sort(([, a], [, b]) => b - a).slice(0, 5);
    const max = entries.length > 0 ? entries[0][1] : 1;
    const total = Object.values(data).reduce((s, v) => s + v, 0);

    return (
        <div className={`bg-slate-800/40 backdrop-blur-sm border ${style.ring} rounded-xl p-4 flex flex-col`}>
            <div className="flex items-start justify-between mb-3">
                <div>
                    <div className="text-sm font-semibold text-white">{title}</div>
                    <div className="text-[10px] text-white/40 uppercase tracking-wider">{subtitle}</div>
                </div>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${style.iconBg}`}>
                    <Icon className="w-4 h-4" />
                </div>
            </div>
            {entries.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-xs text-white/30 py-6">
                    No data yet
                </div>
            ) : (
                <div className="space-y-2 flex-1">
                    {entries.map(([name, count], idx) => (
                        <div key={name}>
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-white/30 w-3">{idx + 1}</span>
                                <span className="text-white/80 flex-1 truncate">{name}</span>
                                <span className={`font-semibold ${style.text}`}>
                                    {count.toLocaleString()}{valueLabel ? ` ${valueLabel}` : ''}
                                </span>
                            </div>
                            <div className="h-1 bg-white/5 rounded-full overflow-hidden mt-1 ml-5">
                                <div
                                    className={`h-full bg-gradient-to-r ${style.bar} rounded-full transition-all`}
                                    style={{ width: `${(count / max) * 100}%` }}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {total > 0 && (
                <div className="mt-3 pt-2 border-t border-white/5 text-[10px] text-white/40 flex justify-between">
                    <span>Total</span>
                    <span className={`font-semibold ${style.text}`}>{total.toLocaleString()}</span>
                </div>
            )}
        </div>
    );
}

export default function StaffDashboard() {
    const navigate = useNavigate();
    const { requestId: urlRequestId } = useParams<{ requestId?: string }>();
    const { user, logout } = useAuth();
    const { settings, demoMode } = useSettings();
    const contentRef = useRef<HTMLDivElement>(null);
    const [demoToast, setDemoToast] = useState<string | null>(null);

    // Demo mode guard — shows toast and blocks action
    const demoGuard = () => {
        if (demoMode) {
            setDemoToast('🔒 Demo mode — changes are disabled. Deploy your own instance to manage requests.');
            setTimeout(() => setDemoToast(null), 4000);
            return true;
        }
        return false;
    };

    // Handle browser back/forward navigation
    const handleHashChange = useCallback((hash: string) => {
        // Parse hash: could be 'dashboard', 'statistics', 'active', 'active/request/SR-123', 'detail/SR-123', etc.
        const parts = hash.split("/");
        const view = parts[0] as View;
        const staffRestrictedViews = ['statistics', 'reports', 'deleted_tickets'];
        const validViews: View[] = ['dashboard', 'active', 'in_progress', 'resolved', 'all_tickets', 'statistics', 'reports', 'report', 'deleted_tickets'];
        if (user?.role === 'staff' && staffRestrictedViews.includes(view)) {
            setCurrentView('dashboard');
            return;
        }

        // Handle detail/{id} format (from similar reports buttons)
        if (parts[0] === 'detail' && parts[1]) {
            loadRequestDetail(parts[1]);
            return;
        }

        if (validViews.includes(view)) {
            setCurrentView(view);
            if (parts[1] === 'request' && parts[2]) {
                // Has request ID - load it
                loadRequestDetail(parts[2]);
            } else {
                // No request - clear selection
                setSelectedRequest(null);
            }
        } else if (!hash) {
            // Empty hash - go to dashboard
            setCurrentView('dashboard');
            setSelectedRequest(null);
        }
    }, []);

    // URL hashing, dynamic titles, and scroll-to-top
    const { updateHash, updateTitle, scrollToTop } = usePageNavigation({
        baseTitle: settings?.township_name ? `Staff Portal | ${settings.township_name}` : 'Staff Portal',
        scrollContainerRef: contentRef,
        onHashChange: handleHashChange,
    });

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentView, setCurrentView] = useState<View>('dashboard');
    const [requests, setRequests] = useState<ServiceRequest[]>([]);
    const [allRequests, setAllRequests] = useState<ServiceRequest[]>([]); // For dashboard map
    const [selectedRequest, setSelectedRequest] = useState<ServiceRequestDetail | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [visibleCount, setVisibleCount] = useState(60); // virtual window: render this many rows
    const [searchQuery, setSearchQuery] = useState('');
    const [showIntakeModal, setShowIntakeModal] = useState(false);
    const [services, setServices] = useState<ServiceDefinition[]>([]);
    const [statistics, setStatistics] = useState<Statistics | null>(null);
    const [advancedStats, setAdvancedStats] = useState<AdvancedStatistics | null>(null);
    const [heatmapData, setHeatmapData] = useState<HeatmapData | null>(null);
    const [staffActivity, setStaffActivity] = useState<{
        top_assignees: Record<string, number>;
        top_resolvers: Record<string, number>;
        top_status_changers: Record<string, number>;
        top_commenters: Record<string, number>;
        top_assigners: Record<string, number>;
        totals: { status_changes: number; comments: number };
    } | null>(null);
    const [statsStartDate, setStatsStartDate] = useState('');
    const [statsEndDate, setStatsEndDate] = useState('');
    const [statsFilterLoading, setStatsFilterLoading] = useState(false);
    const [statsDatePickerOpen, setStatsDatePickerOpen] = useState(false);

    // Dashboard-specific state
    const [departments, setDepartments] = useState<Department[]>([]);
    const [users, setUsers] = useState<UserType[]>([]);
    const [mapLayers, setMapLayers] = useState<MapLayer[]>([]);
    const [mapsConfig, setMapsConfig] = useState<{ google_maps_api_key: string | null; google_maps_map_id: string | null; township_boundary: object | null; default_center?: { lat: number; lng: number } } | null>(null);

    // Intake form state
    const [intakeData, setIntakeData] = useState({
        service_code: '',
        description: '',
        address: '',
        lat: null as number | null,
        long: null as number | null,
        first_name: '',
        last_name: '',
        phone: '',
        media_urls: [] as string[],
        source: 'staff_observation' as 'phone' | 'walk_in' | 'email' | 'staff_observation',
        hidden_from_public: true,
    });
    const [intakeSubmitting, setIntakeSubmitting] = useState(false);
    const [intakeSubmitError, setIntakeSubmitError] = useState<string | null>(null);
    const [intakeSuccess, setIntakeSuccess] = useState<string | null>(null);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [intakeOutsideBoundary, setIntakeOutsideBoundary] = useState(false);
    const [reportTab, setReportTab] = useState<'submit' | 'my'>('submit');
    const [mySubmissions, setMySubmissions] = useState<ServiceRequest[]>([]);
    const [mySubmissionsLoading, setMySubmissionsLoading] = useState(false);
    const [subSort, setSubSort] = useState<'newest' | 'oldest' | 'status' | 'service' | 'updated'>('newest');
    const [subPage, setSubPage] = useState(1);
    const [subPageSize, setSubPageSize] = useState(10);
    const [subStatusFilter, setSubStatusFilter] = useState('');
    const [subSearch, setSubSearch] = useState('');
    const [subServiceFilter, setSubServiceFilter] = useState('');
    const [subDateFrom, setSubDateFrom] = useState('');
    const [subDateTo, setSubDateTo] = useState('');

    // Comments state
    const [comments, setComments] = useState<RequestComment[]>([]);
    const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
    const [newComment, setNewComment] = useState('');
    const [commentVisibility, setCommentVisibility] = useState<'internal' | 'external'>('internal');
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const [commentPhotoUrl, setCommentPhotoUrl] = useState<string | null>(null);
    const [commentPhotoUploading, setCommentPhotoUploading] = useState(false);
    const commentFileInputRef = React.useRef<HTMLInputElement>(null);

    // Delete modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteJustification, setDeleteJustification] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    // Merge modal state
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [mergeSearchQuery, setMergeSearchQuery] = useState('');
    const [mergeSelectedIds, setMergeSelectedIds] = useState<string[]>([]);
    const [mergeComment, setMergeComment] = useState('');
    const [isMerging, setIsMerging] = useState(false);
    const [mergeError, setMergeError] = useState<string | null>(null);

    // Unmerge state
    const [isUnmerging, setIsUnmerging] = useState(false);

    // Deleted tickets state
    const [deletedRequests, setDeletedRequests] = useState<ServiceRequest[]>([]);
    const [deletedLoading, setDeletedLoading] = useState(false);
    const [deletedError, setDeletedError] = useState<string | null>(null);
    const [restoringId, setRestoringId] = useState<string | null>(null);
    const [deletedSearch, setDeletedSearch] = useState('');
    const [deletedFilterCategory, setDeletedFilterCategory] = useState('');
    const [deletedFilterDeletedBy, setDeletedFilterDeletedBy] = useState('');
    const [deletedPage, setDeletedPage] = useState(1);
    const deletedPageSize = 10;

    // Closed substatus state
    const [showClosedModal, setShowClosedModal] = useState(false);
    const [closedSubstatus, setClosedSubstatus] = useState<ClosedSubstatus>('resolved');
    const [completionMessage, setCompletionMessage] = useState('');
    const [completionPhotoUrl, setCompletionPhotoUrl] = useState('');

    // Lightbox modal state
    const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

    // Assignment editing state
    const [editAssignment, setEditAssignment] = useState<{ departmentId: number | null; assignedTo: string | null } | null>(null);
    const [isSavingAssignment, setIsSavingAssignment] = useState(false);

    // Filter states
    const [filterDepartment, setFilterDepartment] = useState<number | null>(null);
    const [filterService, setFilterService] = useState<string | null>(null);
    const [filterAssignment, setFilterAssignment] = useState<'all' | 'me' | 'department'>('all');
    const [filterPhone, setFilterPhone] = useState<string>('');
    const [filterDateFrom, setFilterDateFrom] = useState<string>('');
    const [filterDateTo, setFilterDateTo] = useState<string>('');
    const [filterSource, setFilterSource] = useState<'all' | 'staff' | 'resident'>('all');
    const [filterStaff, setFilterStaff] = useState<string>('');
    const [showFilters, setShowFilters] = useState(false);
    const [sortOrder, setSortOrder] = useState<'newest' | 'oldest' | 'updated' | 'priority_high' | 'priority_low' | 'alpha_az' | 'alpha_za' | 'reporter_name' | 'reporter_phone' | 'address'>('newest');

    // Asset-related requests (for matched assets)
    type AssetRelatedRequest = { service_request_id: string; service_name: string; status: string; requested_datetime: string; address: string; description: string; };
    const [assetRelatedRequests, setAssetRelatedRequests] = useState<AssetRelatedRequest[]>([]);
    const [isLoadingAssetHistory, setIsLoadingAssetHistory] = useState(false);

    // Share link state
    const [showShareMenu, setShowShareMenu] = useState(false);
    const [copiedLink, setCopiedLink] = useState<'staff' | 'resident' | null>(null);

    // Detail panel ref for scroll-to-top on request selection
    const detailPanelRef = useRef<HTMLDivElement>(null);
    const listSentinelRef = useRef<HTMLDivElement>(null); // intersection target for load-more

    // Priority editing state
    const [showPriorityEditor, setShowPriorityEditor] = useState(false);
    const [pendingPriority, setPendingPriority] = useState<number | null>(null);
    const [isUpdatingPriority, setIsUpdatingPriority] = useState(false);

    // AI section collapse state (collapsed by default to save space)
    const [isAIExpanded, setIsAIExpanded] = useState(false);

    // Map priority filter state ('all', 'high', 'medium', 'low')
    const [mapPriorityFilter, setMapPriorityFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');

    // Notification settings modal state
    const [showNotificationSettings, setShowNotificationSettings] = useState(false);

    // Activity feed state
    const [showActivityFeed, setShowActivityFeed] = useState(false);

    // AI Analytics Chat state
    const [chatOpen, setChatOpen] = useState(false);
    const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'assistant'; content: string }[]>([]);
    const [chatInput, setChatInput] = useState('');
    const [chatLoading, setChatLoading] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);


    // Scroll chat to bottom when new messages arrive
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [chatMessages, chatLoading]);

    const sendChatMessage = async (e?: FormEvent) => {
        e?.preventDefault();
        const msg = chatInput.trim();
        if (!msg || chatLoading) return;
        const newMessages = [...chatMessages, { role: 'user' as const, content: msg }];
        setChatMessages(newMessages);
        setChatInput('');
        setChatLoading(true);
        try {
            const result = await api.analyticsChat(msg, newMessages.slice(0, -1));
            setChatMessages(prev => [...prev, { role: 'assistant', content: result.response }]);
        } catch (err: any) {
            setChatMessages(prev => [...prev, { role: 'assistant', content: `⚠️ Error: ${err.message || 'Failed to get AI response'}` }]);
        } finally {
            setChatLoading(false);
        }
    };

    // Get current user's department IDs
    const userDepartmentIds = useMemo(() => {
        return user?.departments?.map(d => d.id) || [];
    }, [user]);

    // Filtered and sorted requests based on current view and filters
    const filteredSortedRequests = useMemo(() => {
        // First, filter by status based on current view
        let filtered = allRequests.filter(r => {
            if (currentView === 'active') return r.status === 'open';
            if (currentView === 'in_progress') return r.status === 'in_progress';
            if (currentView === 'resolved') return r.status === 'closed';
            // 'all_tickets' and 'dashboard' show all
            return true;
        });

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(r =>
                r.service_request_id.toLowerCase().includes(query) ||
                r.description?.toLowerCase().includes(query) ||
                r.address?.toLowerCase().includes(query) ||
                r.service_name?.toLowerCase().includes(query)
            );
        }

        // Apply department filter
        if (filterDepartment !== null) {
            filtered = filtered.filter(r => r.assigned_department_id === filterDepartment);
        }

        // Apply service filter
        if (filterService !== null) {
            filtered = filtered.filter(r => r.service_code === filterService);
        }

        // Apply assignment filter
        if (filterAssignment === 'me' && user) {
            filtered = filtered.filter(r => r.assigned_to === user.username);
        } else if (filterAssignment === 'department') {
            if (userDepartmentIds.length > 0) {
                // Show ALL requests assigned to my department
                filtered = filtered.filter(r =>
                    r.assigned_department_id &&
                    userDepartmentIds.includes(r.assigned_department_id)
                );
            }
            // If no departments configured, show all requests (no additional filter)
        }

        // Apply phone filter
        if (filterPhone.trim()) {
            const ph = filterPhone.trim().toLowerCase();
            filtered = filtered.filter(r => (r.phone || '').toLowerCase().includes(ph));
        }

        // Apply date range filter
        if (filterDateFrom) {
            const from = new Date(filterDateFrom).getTime();
            filtered = filtered.filter(r => new Date(r.requested_datetime).getTime() >= from);
        }
        if (filterDateTo) {
            // Include the full "to" day by going to end of that day
            const to = new Date(filterDateTo + 'T23:59:59').getTime();
            filtered = filtered.filter(r => new Date(r.requested_datetime).getTime() <= to);
        }

        // Apply source filter
        if (filterSource === 'staff') {
            filtered = filtered.filter(r => r.source === 'staff_observation');
        } else if (filterSource === 'resident') {
            filtered = filtered.filter(r => r.source !== 'staff_observation');
        }

        // Apply staff member filter: match assigned_to OR reporter name for staff_observation reports
        if (filterStaff) {
            const staffUser = users.find(u => u.username === filterStaff);
            const staffFirstName = (staffUser?.full_name || staffUser?.username || '').split(' ')[0].toLowerCase();
            filtered = filtered.filter(r =>
                r.assigned_to === filterStaff ||
                (r.source === 'staff_observation' && staffFirstName &&
                    (r.first_name || '').toLowerCase() === staffFirstName)
            );
        }

        // Sort based on selected sort order
        filtered.sort((a, b) => {
            const getPriority = (r: ServiceRequest) =>
                r.manual_priority_score ?? ((r.ai_analysis as any)?.priority_score) ?? 5;
            const getReporterName = (r: ServiceRequest) =>
                [r.first_name, r.last_name].filter(Boolean).join(' ').toLowerCase() || '';

            switch (sortOrder) {
                case 'oldest':
                    return new Date(a.requested_datetime).getTime() - new Date(b.requested_datetime).getTime();
                case 'updated':
                    return new Date(b.updated_datetime || b.requested_datetime).getTime() - new Date(a.updated_datetime || a.requested_datetime).getTime();
                case 'priority_high':
                    return getPriority(b) - getPriority(a);
                case 'priority_low':
                    return getPriority(a) - getPriority(b);
                case 'alpha_az':
                    return (a.service_name || '').localeCompare(b.service_name || '');
                case 'alpha_za':
                    return (b.service_name || '').localeCompare(a.service_name || '');
                case 'reporter_name':
                    return getReporterName(a).localeCompare(getReporterName(b));
                case 'reporter_phone':
                    return (a.phone || '').localeCompare(b.phone || '');
                case 'address':
                    return (a.address || '').localeCompare(b.address || '');
                case 'newest':
                default:
                    return new Date(b.requested_datetime).getTime() - new Date(a.requested_datetime).getTime();
            }
        });

        return filtered;
    }, [allRequests, currentView, searchQuery, filterDepartment, filterService, filterAssignment, filterPhone, filterDateFrom, filterDateTo, filterSource, filterStaff, user, userDepartmentIds, sortOrder]);

    // Reset visible window whenever filters/sort change
    useEffect(() => {
        setVisibleCount(60);
    }, [searchQuery, filterDepartment, filterService, filterAssignment, filterPhone,
        filterDateFrom, filterDateTo, filterSource, filterStaff, sortOrder, mapPriorityFilter, currentView]);

    // IntersectionObserver: extend the visible window when sentinel scrolls into view
    useEffect(() => {
        const sentinel = listSentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisibleCount(prev => prev + 40);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [filteredSortedRequests.length]); // re-bind when list length changes

    // Quick stats for the current view
    const quickStats = useMemo(() => {
        const viewRequests = allRequests.filter(r => {
            if (currentView === 'active') return r.status === 'open';
            if (currentView === 'in_progress') return r.status === 'in_progress';
            if (currentView === 'resolved') return r.status === 'closed';
            return true;
        });

        const assignedToMe = viewRequests.filter(r => user && r.assigned_to === user.username).length;
        const total = viewRequests.length;
        // Count requests in my department(s) — if no depts configured, count all
        const inMyDepartment = userDepartmentIds.length > 0
            ? viewRequests.filter(r =>
                r.assigned_department_id && userDepartmentIds.includes(r.assigned_department_id)
            ).length
            : total;

        return { assignedToMe, inMyDepartment, total };
    }, [allRequests, currentView, user, userDepartmentIds]);

    // Helper to get effective priority score (checks multiple sources)
    const getEffectivePriority = (r: ServiceRequest): number => {
        // Priority precedence: manual > ai_analysis.priority_score > default 5
        if (r.manual_priority_score != null) return r.manual_priority_score;
        // Check nested ai_analysis for priority_score (AI suggestion)
        const aiAnalysis = r.ai_analysis as any;
        if (aiAnalysis?.priority_score != null) return aiAnalysis.priority_score;
        return 5; // Default
    };

    // Filter requests by priority for the map and list
    const mapFilteredRequests = useMemo(() => {
        if (mapPriorityFilter === 'all') return allRequests;
        return allRequests.filter(r => {
            const priority = getEffectivePriority(r);
            if (mapPriorityFilter === 'high') return priority >= 8;
            if (mapPriorityFilter === 'medium') return priority >= 5 && priority < 8;
            if (mapPriorityFilter === 'low') return priority < 5;
            return true;
        });
    }, [allRequests, mapPriorityFilter]);

    // Clear all filters
    const clearFilters = () => {
        setSearchQuery('');
        setFilterDepartment(null);
        setFilterService(null);
        setFilterAssignment("all");
        setFilterPhone('');
        setFilterDateFrom('');
        setFilterDateTo('');
        setFilterSource('all');
        setFilterStaff('');
        setMapPriorityFilter('all');
    };

    const hasActiveFilters = searchQuery.trim() || filterDepartment !== null || filterService !== null || filterAssignment !== 'all' || filterPhone.trim() || filterDateFrom || filterDateTo || filterSource !== 'all' || filterStaff || mapPriorityFilter !== 'all';

    useEffect(() => {
        // Initial load - only fetch once
        loadInitialData();
    }, []);

    // Auto-refresh requests every 30 seconds for live updates
    useEffect(() => {
        const refreshRequests = async () => {
            try {
                const freshRequests = await api.getRequests();
                setAllRequests(freshRequests);
                // Re-apply current filter by updating requests state
                // The filtered list will update via the existing useMemo
            } catch (err) {
                console.error('Auto-refresh failed:', err);
            }
        };

        // Polling interval
        const pollInterval = setInterval(refreshRequests, 30000); // 30 seconds

        // Also refresh when tab becomes visible
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshRequests();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            clearInterval(pollInterval);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    // Separate effect for statistics (only loads when needed)
    useEffect(() => {
        if (currentView === 'statistics' && !statistics) {
            loadStatistics();
        }
        if (currentView === 'deleted_tickets') {
            loadDeletedTickets();
        }
    }, [currentView]);

    // Auto-load request if URL contains requestId
    useEffect(() => {
        if (urlRequestId) {
            loadRequestDetail(urlRequestId);
        }
    }, [urlRequestId]);

    // Update URL hash and title when view changes
    useEffect(() => {
        updateHash(currentView);
        const viewTitles: Record<View, string> = {
            dashboard: 'Dashboard',
            active: 'Open Requests',
            in_progress: 'In Progress',
            resolved: 'Resolved',
            all_tickets: 'All Tickets',
            statistics: 'Statistics',
            reports: 'Reports',
            report: 'Report a Problem',
        };
        updateTitle(viewTitles[currentView]);
        scrollToTop('instant');
        // Reset report page tab when navigating away and back
        if (currentView !== 'report') { setReportTab('submit'); setSubPage(1); setSubSearch(''); setSubStatusFilter(''); setSubServiceFilter(''); setSubDateFrom(''); setSubDateTo(''); }
    }, [currentView, updateHash, updateTitle, scrollToTop]);

    // Load my submissions when switching to the My Submissions tab
    useEffect(() => {
        if (currentView === 'report' && reportTab === 'my') {
            setMySubmissionsLoading(true);
            api.getMySubmissions()
                .then(setMySubmissions)
                .catch(() => setMySubmissions([]))
                .finally(() => setMySubmissionsLoading(false));
        }
    }, [currentView, reportTab]);

    // Update URL hash when request is selected
    useEffect(() => {
        if (selectedRequest) {
            updateHash(`${currentView}/request/${selectedRequest.service_request_id}`);
            updateTitle(`Request ${selectedRequest.service_request_id}`);
            // Scroll detail panel to top when a new request is selected
            if (detailPanelRef.current) {
                detailPanelRef.current.scrollTo({ top: 0, behavior: 'instant' });
            }
        }
    }, [selectedRequest, currentView, updateHash, updateTitle]);

    // Lock body scroll when mobile overlay is open to prevent scroll bleed-through
    useEffect(() => {
        if (selectedRequest && window.innerWidth < 1024) {
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
        } else {
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.touchAction = '';
        };
    }, [selectedRequest]);

    // Load asset-related requests when selected request has a matched_asset
    useEffect(() => {
        const loadAssetHistory = async () => {
            const matchedAsset = (selectedRequest as any)?.matched_asset;
            if (matchedAsset?.asset_id) {
                setIsLoadingAssetHistory(true);
                try {
                    const related = await api.getAssetRelatedRequests(
                        matchedAsset.asset_id,
                        selectedRequest?.service_request_id
                    );
                    setAssetRelatedRequests(related);
                } catch (err) {
                    console.error('Failed to load asset history:', err);
                    setAssetRelatedRequests([]);
                } finally {
                    setIsLoadingAssetHistory(false);
                }
            } else {
                setAssetRelatedRequests([]);
            }
        };
        loadAssetHistory();
    }, [selectedRequest]);

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            const [allRequestsData, servicesData, depts, usersData, layers, config] = await Promise.all([
                api.getRequests(), // Fetch ALL requests once
                api.getServices(),
                api.getDepartments(),
                api.getStaffMembers(), // Staff-accessible endpoint
                api.getMapLayers(),
                api.getMapsConfig(),
            ]);
            setAllRequests(allRequestsData);
            setRequests(allRequestsData); // Initial set
            setServices(servicesData);
            setDepartments(depts);
            setUsers(usersData);
            setMapLayers(layers);
            setMapsConfig(config);
        } catch (err) {
            console.error('Failed to load data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const loadDeletedTickets = async () => {
        setDeletedLoading(true);
        setDeletedError(null);
        try {
            const data = await api.getRequests(undefined, true);
            setDeletedRequests(data.filter((r: ServiceRequest) => (r as any).deleted_at));
        } catch (err: any) {
            setDeletedError(err?.message || 'Failed to load deleted tickets');
        } finally {
            setDeletedLoading(false);
        }
    };

    const handleRestoreTicket = async (requestId: string) => {
        setRestoringId(requestId);
        try {
            await api.restoreRequest(requestId);
            setDeletedRequests(prev => prev.filter(r => r.service_request_id !== requestId));
        } catch (err: any) {
            alert(`Failed to restore ticket: ${err?.message || 'Unknown error'}`);
        } finally {
            setRestoringId(null);
        }
    };

    const loadStatistics = async (startDate?: string, endDate?: string) => {
        try {
            const params = { start_date: startDate || undefined, end_date: endDate || undefined };
            const [statsData, advancedData, heatmap, activity] = await Promise.all([
                api.getStatistics(params),
                api.getAdvancedStatistics(params),
                api.getHeatmapData().catch(() => null),
                api.getStaffActivitySummary().catch(() => null),
            ]);
            setStatistics(statsData);
            setAdvancedStats(advancedData);
            if (heatmap) setHeatmapData(heatmap);
            if (activity) setStaffActivity(activity);
        } catch (err) {
            console.error('Failed to load statistics:', err);
        }
    };

    const handleStatsDateFilter = async (startDate: string, endDate: string) => {
        setStatsFilterLoading(true);
        try {
            await loadStatistics(startDate || undefined, endDate || undefined);
        } finally {
            setStatsFilterLoading(false);
        }
    };

    const loadRequestDetail = async (requestId: string) => {
        try {
            const detail = await api.getRequestDetail(requestId);
            setSelectedRequest(detail);
            // Load comments and audit log for this request
            loadComments(detail.id);
            loadAuditLog(requestId);
        } catch (err) {
            console.error('Failed to load request detail:', err);
        }
    };

    const handleStatusChange = async (status: string) => {
        if (demoGuard()) return;
        if (!selectedRequest) return;

        // If closing, show modal to select substatus
        if (status === 'closed') {
            setShowClosedModal(true);
            return;
        }

        try {
            const updated = await api.updateRequest(selectedRequest.service_request_id, { status });
            setSelectedRequest(updated);
            // Optimistic update: update list immediately without full reload
            setAllRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
            setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
            loadAuditLog(selectedRequest.service_request_id);
        } catch (err) {
            console.error('Failed to update status:', err);
        }
    };

    const handleCloseWithSubstatus = async () => {
        if (demoGuard()) return;
        if (!selectedRequest) return;
        try {
            const updated = await api.updateRequest(selectedRequest.service_request_id, {
                status: 'closed',
                closed_substatus: closedSubstatus,
                completion_message: completionMessage || undefined,
                completion_photo_url: closedSubstatus === 'resolved' ? completionPhotoUrl || undefined : undefined,
            });
            setSelectedRequest(updated);
            // Optimistic update: update list immediately without full reload
            setAllRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
            setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
            setShowClosedModal(false);
            setClosedSubstatus('resolved');
            setCompletionMessage('');
            setCompletionPhotoUrl('');
            loadAuditLog(selectedRequest.service_request_id);
        } catch (err) {
            console.error('Failed to close request:', err);
        }
    };

    const loadComments = async (requestId: number) => {
        try {
            const commentsData = await api.getComments(requestId);
            setComments(commentsData);
        } catch (err) {
            console.error('Failed to load comments:', err);
        }
    };

    const loadAuditLog = async (requestId: string) => {
        try {
            const logData = await api.getAuditLog(requestId);
            setAuditLog(logData);
        } catch (err) {
            // Fallback - audit log may not exist for older requests
            setAuditLog([]);
        }
    };

    const handleAddComment = async () => {
        if (demoGuard()) return;
        if (!selectedRequest || (!newComment.trim() && !commentPhotoUrl)) return;
        setIsSubmittingComment(true);
        try {
            await api.createComment(selectedRequest.id, newComment.trim() || '📷', commentVisibility, commentPhotoUrl);
            setNewComment('');
            setCommentPhotoUrl(null);
            loadComments(selectedRequest.id);
        } catch (err) {
            console.error('Failed to add comment:', err);
        } finally {
            setIsSubmittingComment(false);
        }
    };

    const handleCommentPhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCommentPhotoUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const token = localStorage.getItem('token');
            const res = await fetch('/api/system/upload/image', {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: formData,
            });
            if (!res.ok) throw new Error('Upload failed');
            const data = await res.json();
            setCommentPhotoUrl(data.url);
        } catch (err) {
            console.error('Failed to upload photo:', err);
        } finally {
            setCommentPhotoUploading(false);
            if (commentFileInputRef.current) commentFileInputRef.current.value = '';
        }
    };

    const handleDeleteRequest = async () => {
        if (demoGuard()) return;
        if (!selectedRequest || !deleteJustification.trim()) return;
        setIsDeleting(true);
        try {
            await api.deleteRequest(selectedRequest.service_request_id, deleteJustification.trim());
            setShowDeleteModal(false);
            setDeleteJustification('');
            setSelectedRequest(null);
            loadInitialData();
        } catch (err) {
            console.error('Failed to delete request:', err);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleMergeTickets = async () => {
        if (demoGuard()) return;
        if (!selectedRequest || mergeSelectedIds.length === 0) return;
        setIsMerging(true);
        setMergeError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/open311/v2/requests/${selectedRequest.service_request_id}/merge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: JSON.stringify({ source_ids: mergeSelectedIds, comment: mergeComment }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || 'Merge failed');
            setShowMergeModal(false);
            setMergeSelectedIds([]);
            setMergeComment('');
            setMergeSearchQuery('');
            loadInitialData();
            loadComments(selectedRequest.id);
        } catch (err: any) {
            setMergeError(err?.message || 'Merge failed');
        } finally {
            setIsMerging(false);
        }
    };

    const handleUnmerge = async () => {
        if (demoGuard()) return;
        if (!selectedRequest) return;
        if (!confirm(`Un-merge ticket ${selectedRequest.service_request_id}? It will be restored to Open status.`)) return;
        setIsUnmerging(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`/api/open311/v2/requests/${selectedRequest.service_request_id}/unmerge`, {
                method: 'POST',
                headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.detail || 'Unmerge failed');
            loadInitialData();
        } catch (err: any) {
            alert(err?.message || 'Unmerge failed');
        } finally {
            setIsUnmerging(false);
        }
    };

    const handleCreateIntake = async (e: React.FormEvent) => {
        e.preventDefault();
        if (demoGuard()) return;
        setIntakeSubmitting(true);
        setIntakeSubmitError(null);
        try {
            const result = await api.createManualIntake({
                ...intakeData,
                source: intakeData.source as 'phone' | 'walk_in' | 'email' | 'staff_observation',
            });
            setIntakeSuccess(`Report submitted — ID: ${result.service_request_id}`);
            setIntakeData({
                service_code: '',
                description: '',
                address: '',
                lat: null,
                long: null,
                first_name: '',
                last_name: '',
                phone: '',
                media_urls: [],
                source: 'staff_observation',
                hidden_from_public: true,
            });
            loadInitialData();
        } catch (err: any) {
            setIntakeSubmitError(err?.message || 'Failed to submit report. Please try again.');
        } finally {
            setIntakeSubmitting(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Old simple search filter removed - now using filteredSortedRequests above

    const getCounts = () => {
        const open = requests.filter((r) => r.status === 'open').length;
        const inProgress = requests.filter((r) => r.status === 'in_progress').length;
        const closed = requests.filter((r) => r.status === 'closed').length;
        return { open, inProgress, closed, total: requests.length };
    };

    const counts = getCounts();

    const menuItems = [
        { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', count: null },
        { id: 'active', icon: AlertCircle, label: 'Open', count: counts.open },
        { id: 'in_progress', icon: Clock, label: 'In Progress', count: counts.inProgress },
        { id: 'resolved', icon: CheckCircle, label: 'Completed', count: counts.closed },
        { id: 'all_tickets', icon: FileText, label: 'All Tickets', count: counts.total },
        ...(user?.role !== 'staff' ? [
            { id: 'statistics', icon: BarChart3, label: 'Statistics', count: null },
            { id: 'reports', icon: FileText, label: 'Reports', count: null },
        ] as const : []),
        ...(user?.role === 'admin' || user?.role === 'staff_admin' || user?.role === 'org_admin' ? [
            { id: 'deleted_tickets', icon: Trash2, label: 'Deleted Tickets', count: null },
        ] as const : []),
        { id: 'report', icon: PlusCircle, label: 'Report a Problem', count: null },
    ];

    // Use the comprehensive filteredSortedRequests (defined above) for the list
    const sortedRequests = filteredSortedRequests;

    // Calculate dashboard stats
    const dashboardStats = useMemo(() => {
        const myRequests = allRequests.filter(r => (r as any).assigned_to === user?.username);
        const myActive = myRequests.filter(r => r.status === 'open').length;
        const myInProgress = myRequests.filter(r => r.status === 'in_progress').length;

        // Department requests - filter by user's department IDs
        const userDeptIds = user?.departments?.map(d => d.id) || [];
        const deptRequests = allRequests.filter(r =>
            userDeptIds.includes(r.assigned_department_id as number)
        );
        const deptActive = deptRequests.filter(r => r.status === 'open').length;

        return {
            myActive,
            myInProgress,
            deptActive,
            totalActive: counts.open,
            totalInProgress: counts.inProgress,
        };
    }, [allRequests, user, counts]);

    const handleMapRequestSelect = (requestId: string) => {
        loadRequestDetail(requestId);
        setCurrentView('active'); // Switch to list view to see details
    };

    // Export handlers with proper error handling
    const handleExportRequests = async (format: 'csv' | 'json' | 'geojson', params?: { start_date?: string; end_date?: string; status?: string; service_code?: string; department_id?: string; priority?: string; merged_only?: boolean; include_pii?: boolean }) => {
        try {
            await api.exportRequests({ format, ...params });
        } catch (err: any) {
            console.error('Export failed:', err);
            alert(`Export failed: ${err.message || 'Unknown error'}. Please try again.`);
        }
    };

    const handleExportStatistics = async (format: 'csv' | 'json', params?: { start_date?: string; end_date?: string }) => {
        try {
            await api.exportStatistics({ format, ...params });
        } catch (err: any) {
            console.error('Statistics export failed:', err);
            alert(`Export failed: ${err.message || 'Unknown error'}. Please try again.`);
        }
    };

    return (
        <div className="h-screen flex overflow-hidden">
            {/* Mobile sidebar backdrop */}
            <AnimatePresence>
                {sidebarOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setSidebarOpen(false)}
                        className="fixed inset-0 bg-black/60 z-40 lg:hidden"
                    />
                )}
            </AnimatePresence>

            {/* Sidebar - Fixed position on both mobile and desktop */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-72 glass-sidebar transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    }`}
                aria-label="Staff portal navigation"
            >
                <div className="flex flex-col h-full overflow-y-auto">
                    {/* Sidebar Header */}
                    <div className="p-6 border-b border-white/10">
                        <div className="flex items-center justify-between">
                            <button
                                onClick={() => {
                                    setCurrentView('dashboard');
                                    setSelectedRequest(null);
                                    window.location.hash = '';
                                }}
                                className="group flex items-center gap-3 hover:opacity-80 transition-opacity cursor-pointer"
                                aria-label="Go to dashboard home"
                                title="Go to Home"
                            >
                                {settings?.logo_url ? (
                                    <div className="relative">
                                        <img src={settings.logo_url} alt={`${settings?.township_name || 'Municipality'} logo`} className="h-8 w-auto" />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Home className="w-4 h-4 text-white" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center">
                                        <Home className="w-5 h-5 text-white" />
                                    </div>
                                )}
                                <div className="text-left" data-no-translate>
                                    <h2 className="font-semibold text-white flex items-center gap-1.5">
                                        Staff Command
                                        <Home className="w-3.5 h-3.5 text-white/40 group-hover:text-white/70 transition-colors" />
                                    </h2>
                                    <p className="text-xs text-white/50">{settings?.township_name}</p>
                                </div>
                            </button>
                            <div className="flex items-center gap-2">
                                {/* Activity Feed Bell */}
                                <button
                                    onClick={() => setShowActivityFeed(true)}
                                    className="relative p-2 hover:bg-white/10 rounded-lg transition-colors"
                                    aria-label="Open activity feed"
                                >
                                    <Bell className="w-5 h-5 text-white/60" aria-hidden="true" />
                                    {/* Calculate and show unread count */}
                                    {(() => {
                                        const now = Date.now();
                                        const twentyFourHours = 24 * 60 * 60 * 1000;
                                        const readItems = JSON.parse(localStorage.getItem('activityFeedRead') || '[]');
                                        const readSet = new Set(readItems);
                                        let count = 0;
                                        allRequests.forEach(req => {
                                            const age = now - new Date(req.requested_datetime).getTime();
                                            if (age < twentyFourHours && req.assigned_department_id && userDepartmentIds.includes(req.assigned_department_id)) {
                                                if (!readSet.has(`new-${req.service_request_id}`)) count++;
                                            }
                                        });
                                        return count > 0 ? (
                                            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center animate-pulse">
                                                {count > 9 ? '9+' : count}
                                            </span>
                                        ) : null;
                                    })()}
                                </button>
                                <button
                                    onClick={() => setSidebarOpen(false)}
                                    className="lg:hidden p-2 hover:bg-white/10 rounded-lg"
                                    aria-label="Close navigation menu"
                                >
                                    <X className="w-5 h-5 text-white/60" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Menu Items */}
                    <nav className="flex-1 p-4 space-y-2">
                        <p className="text-xs font-medium text-white/40 uppercase tracking-wider px-3 mb-3">
                            Main
                        </p>
                        {menuItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => {
                                    setCurrentView(item.id as View);
                                    setSelectedRequest(null);
                                    setSidebarOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-colors ${currentView === item.id
                                    ? 'bg-primary-500/20 text-white'
                                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                                    }`}
                            >
                                <div className="flex items-center gap-3">
                                    <item.icon className="w-5 h-5" />
                                    <span className="font-medium">{item.label}</span>
                                </div>
                                {item.count !== null && (
                                    <Badge variant={currentView === item.id ? 'info' : 'default'}>
                                        {item.count}
                                    </Badge>
                                )}
                            </button>
                        ))}

                    </nav>

                    {/* User Footer - Sticky */}
                    <div className="sticky bottom-0 p-4 border-t border-white/10 bg-slate-900/90 backdrop-blur-md">
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setShowNotificationSettings(true)}
                                className="flex items-center gap-3 flex-1 min-w-0 p-2 -m-2 hover:bg-white/5 rounded-lg transition-colors cursor-pointer"
                                title="Notification Settings"
                            >
                                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-primary-500/30 flex items-center justify-center text-white font-medium">
                                    {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                                </div>
                                <div className="min-w-0 flex-1 overflow-hidden text-left">
                                    <p className="font-medium text-white text-sm truncate">{user?.full_name || user?.username}</p>
                                    <p className="text-xs text-white/50 capitalize">{user?.role}</p>
                                    {user?.departments && user.departments.length > 0 && (
                                        <p className="text-xs text-primary-400 truncate mt-0.5">{user.departments.map(d => d.name).join(', ')}</p>
                                    )}
                                </div>
                            </button>
                            {user?.role === 'admin' && (
                                <button
                                    onClick={() => window.location.href = '/admin'}
                                    className="flex items-center gap-1.5 px-2 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-lg hover:bg-amber-500/30 transition-colors"
                                    title="Admin Console"
                                    aria-label="Go to Admin Console"
                                >
                                    <Settings className="w-4 h-4 text-amber-400" aria-hidden="true" />
                                    <span className="text-xs font-medium text-amber-400">Admin</span>
                                </button>
                            )}
                            <button
                                onClick={handleLogout}
                                className="flex-shrink-0 p-2 hover:bg-white/10 rounded-lg transition-colors"
                                title="Sign out"
                                aria-label="Sign out"
                            >
                                <LogOut className="w-5 h-5 text-white/60" aria-hidden="true" />
                            </button>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main Content - offset by sidebar width on desktop */}
            <div id="main-content" role="main" className="flex-1 flex flex-col min-w-0 lg:ml-72">
                {/* Mobile Header */}
                <header className="lg:hidden glass-sidebar p-4 flex items-center justify-between sticky top-0 z-30">
                    <button
                        onClick={() => setSidebarOpen(true)}
                        className="p-2 hover:bg-white/10 rounded-lg"
                        aria-label="Open navigation menu"
                    >
                        <Menu className="w-6 h-6 text-white" aria-hidden="true" />
                    </button>
                    <h1 className="font-semibold text-white">Staff Dashboard</h1>
                    <div className="w-10" aria-hidden="true" />
                </header>

                {/* Demo mode banner */}
                {demoMode && (
                    <div className="mx-4 lg:mx-6 mt-4 flex items-center gap-3 p-3 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-300 text-sm">
                        <LockKeyhole className="w-4 h-4 flex-shrink-0" />
                        <span><strong>Demo Mode — View Only.</strong> Browse freely, but changes are disabled.</span>
                    </div>
                )}
                {demoToast && (
                    <div className="mx-4 lg:mx-6 mt-2 flex items-center gap-3 p-3 rounded-xl bg-amber-500/20 border border-amber-500/40 text-amber-200 text-sm animate-pulse">
                        {demoToast}
                    </div>
                )}

                {/* Dashboard View */}
                {currentView === 'dashboard' && (
                    <div className="flex-1 flex flex-col p-4 lg:p-6 overflow-auto">
                        {/* Map Section */}
                        <div className="flex-1 min-h-[400px] lg:min-h-[500px] mb-6 rounded-xl overflow-hidden">
                            <StaffDashboardMap
                                requests={mapFilteredRequests}
                                services={services}
                                departments={departments}
                                users={users}
                                mapLayers={mapLayers}
                                townshipBoundary={mapsConfig?.township_boundary}
                                defaultCenter={mapsConfig?.default_center}
                                onRequestSelect={handleMapRequestSelect}
                            />
                        </div>

                        {/* Stats Cards - Clickable */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            <button
                                onClick={() => setCurrentView('active')}
                                className="text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <Card className="text-center border-primary-500/30 hover:border-primary-500/60 cursor-pointer transition-colors">
                                    <p className="text-3xl font-bold text-primary-400">{dashboardStats.myActive}</p>
                                    <p className="text-white/60 text-sm">Assigned to You</p>
                                </Card>
                            </button>
                            <button
                                onClick={() => setCurrentView('active')}
                                className="text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <Card className="text-center border-blue-500/30 hover:border-blue-500/60 cursor-pointer transition-colors">
                                    <p className="text-3xl font-bold text-blue-200">{dashboardStats.deptActive}</p>
                                    <p className="text-white/60 text-sm">Your Department</p>
                                </Card>
                            </button>
                            <button
                                onClick={() => setCurrentView('active')}
                                className="text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <Card className="text-center border-red-500/30 hover:border-red-500/60 cursor-pointer transition-colors">
                                    <p className="text-3xl font-bold text-red-200">{dashboardStats.totalActive}</p>
                                    <p className="text-white/60 text-sm">All Open</p>
                                </Card>
                            </button>
                            <button
                                onClick={() => setCurrentView('in_progress')}
                                className="text-left transition-all hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <Card className="text-center border-amber-500/30 hover:border-amber-500/60 cursor-pointer transition-colors">
                                    <p className="text-3xl font-bold text-amber-200">{dashboardStats.totalInProgress}</p>
                                    <p className="text-white/60 text-sm">In Progress</p>
                                </Card>
                            </button>
                        </div>
                    </div>
                )}

                {/* Statistics View — Dark Glassmorphism Design */}
                {currentView === 'statistics' && user?.role !== 'staff' && (
                    <div className="flex-1 p-3 sm:p-6 lg:p-8 overflow-auto">
                        <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
                            {/* Header */}
                            <div className="space-y-3">
                                <div>
                                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">Analytics Dashboard</h1>
                                    {advancedStats?.cached_at && !statsStartDate && !statsEndDate && (
                                        <p className="text-xs sm:text-sm text-white/40 mt-1">
                                            Updated {new Date(advancedStats.cached_at.endsWith('Z') ? advancedStats.cached_at : advancedStats.cached_at + 'Z').toLocaleString()}
                                        </p>
                                    )}
                                    {(statsStartDate || statsEndDate) && (
                                        <p className="text-xs sm:text-sm text-primary-400 mt-1">
                                            Filtered: {statsStartDate || '∞'} → {statsEndDate || 'today'}
                                        </p>
                                    )}
                                </div>

                                {/* Date range filter — compact popover */}
                                {(() => {
                                    const today = new Date().toISOString().split('T')[0];
                                    const presets = [
                                        { label: 'Today', sd: today, ed: today },
                                        { label: 'Last 7 days', sd: new Date(Date.now()-6*86400000).toISOString().split('T')[0], ed: today },
                                        { label: 'Last 30 days', sd: new Date(Date.now()-29*86400000).toISOString().split('T')[0], ed: today },
                                        { label: 'Last 90 days', sd: new Date(Date.now()-89*86400000).toISOString().split('T')[0], ed: today },
                                        { label: 'This month', sd: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], ed: today },
                                        { label: 'This year', sd: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0], ed: today },
                                    ];
                                    const matchedPreset = presets.find(p => p.sd === statsStartDate && p.ed === statsEndDate);
                                    const buttonLabel = !statsStartDate && !statsEndDate
                                        ? 'All time'
                                        : matchedPreset?.label
                                            ?? `${statsStartDate || '∞'} → ${statsEndDate || 'today'}`;
                                    const fmt = (d: string) => d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                                    return (
                                        <div className="relative inline-block">
                                            <button
                                                onClick={() => setStatsDatePickerOpen(o => !o)}
                                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all text-sm font-medium ${
                                                    statsStartDate || statsEndDate
                                                        ? 'bg-primary-500/15 border-primary-500/40 text-primary-200 hover:bg-primary-500/20'
                                                        : 'bg-white/5 border-white/15 text-white/80 hover:bg-white/10 hover:border-white/25'
                                                }`}
                                            >
                                                <Calendar className="w-4 h-4" />
                                                <span>{buttonLabel}</span>
                                                {statsFilterLoading
                                                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                    : <ChevronDown className={`w-3.5 h-3.5 transition-transform ${statsDatePickerOpen ? 'rotate-180' : ''}`} />
                                                }
                                            </button>

                                            {statsDatePickerOpen && (
                                                <>
                                                    {/* Click-outside backdrop */}
                                                    <div className="fixed inset-0 z-40" onClick={() => setStatsDatePickerOpen(false)} />
                                                    <div className="absolute left-0 mt-2 w-[320px] sm:w-[400px] bg-gray-900 border border-white/15 rounded-xl shadow-2xl z-50 overflow-hidden">
                                                        <div className="flex">
                                                            {/* Presets column */}
                                                            <div className="w-[140px] border-r border-white/10 bg-black/20 py-2">
                                                                <div className="px-3 py-1.5 text-[10px] font-bold text-white/40 uppercase tracking-wider">Quick</div>
                                                                {presets.map(p => {
                                                                    const active = statsStartDate === p.sd && statsEndDate === p.ed;
                                                                    return (
                                                                        <button
                                                                            key={p.label}
                                                                            onClick={() => { setStatsStartDate(p.sd); setStatsEndDate(p.ed); handleStatsDateFilter(p.sd, p.ed); setStatsDatePickerOpen(false); }}
                                                                            className={`w-full text-left px-3 py-2 text-xs transition-colors ${active ? 'bg-primary-500/20 text-primary-200 font-semibold' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                                                                        >
                                                                            {p.label}
                                                                        </button>
                                                                    );
                                                                })}
                                                                <div className="border-t border-white/10 mt-1 pt-1">
                                                                    <button
                                                                        onClick={() => { setStatsStartDate(''); setStatsEndDate(''); handleStatsDateFilter('', ''); setStatsDatePickerOpen(false); }}
                                                                        className={`w-full text-left px-3 py-2 text-xs transition-colors ${!statsStartDate && !statsEndDate ? 'bg-primary-500/20 text-primary-200 font-semibold' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
                                                                    >
                                                                        All time
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {/* Custom range column */}
                                                            <div className="flex-1 p-4 space-y-3">
                                                                <div className="text-[10px] font-bold text-white/40 uppercase tracking-wider">Custom range</div>
                                                                <div className="space-y-2">
                                                                    <div>
                                                                        <label className="text-[11px] text-white/50 block mb-1">From</label>
                                                                        <input
                                                                            type="date"
                                                                            value={statsStartDate}
                                                                            max={statsEndDate || today}
                                                                            onChange={e => setStatsStartDate(e.target.value)}
                                                                            className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-white text-sm focus:outline-none focus:border-primary-500/60 [color-scheme:dark]"
                                                                        />
                                                                    </div>
                                                                    <div>
                                                                        <label className="text-[11px] text-white/50 block mb-1">To</label>
                                                                        <input
                                                                            type="date"
                                                                            value={statsEndDate}
                                                                            min={statsStartDate || undefined}
                                                                            max={today}
                                                                            onChange={e => setStatsEndDate(e.target.value)}
                                                                            className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/15 text-white text-sm focus:outline-none focus:border-primary-500/60 [color-scheme:dark]"
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {(statsStartDate || statsEndDate) && (
                                                                    <div className="text-[11px] text-white/50 px-1 py-1 bg-white/5 rounded-lg border border-white/5">
                                                                        {fmt(statsStartDate) || '∞'} → {fmt(statsEndDate) || 'today'}
                                                                    </div>
                                                                )}

                                                                <div className="flex items-center gap-2 pt-1">
                                                                    <button
                                                                        onClick={() => { handleStatsDateFilter(statsStartDate, statsEndDate); setStatsDatePickerOpen(false); }}
                                                                        disabled={statsFilterLoading || (!statsStartDate && !statsEndDate)}
                                                                        className="flex-1 px-3 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                                                                    >
                                                                        {statsFilterLoading ? <><RefreshCw className="w-3 h-3 animate-spin" /> Loading</> : <><Check className="w-3.5 h-3.5" /> Apply</>}
                                                                    </button>
                                                                    {(statsStartDate || statsEndDate) && (
                                                                        <button
                                                                            onClick={() => { setStatsStartDate(''); setStatsEndDate(''); }}
                                                                            className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white text-xs transition-colors"
                                                                        >
                                                                            Reset
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })()}
                                <div className="grid grid-cols-3 sm:flex sm:flex-wrap items-center gap-2">
                                    <button
                                        onClick={() => setChatOpen(true)}
                                        className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:from-emerald-700 hover:to-teal-700 transition-all text-xs sm:text-sm font-medium shadow-lg shadow-emerald-500/20"
                                    >
                                        <Sparkles className="w-4 h-4" />
                                        <span>Ask AI</span>
                                    </button>
                                    <button
                                        onClick={() => window.location.href = '/research'}
                                        className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-white/10 border border-white/20 text-white rounded-lg hover:bg-white/20 transition-all text-xs sm:text-sm font-medium"
                                    >
                                        <FlaskConical className="w-4 h-4" />
                                        <span className="hidden sm:inline">Research Portal</span>
                                        <span className="sm:hidden">Research</span>
                                    </button>
                                    <div className="relative group">
                                        <button className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all text-xs sm:text-sm font-medium w-full">
                                            <Download className="w-4 h-4" />
                                            <span>Export</span>
                                            <ChevronDown className="w-3 h-3" />
                                        </button>
                                        <div className="absolute right-0 sm:right-0 mt-2 w-52 bg-gray-900 border border-white/20 rounded-lg shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                                            <div className="p-1.5">
                                                <p className="px-3 py-1.5 text-xs font-semibold text-white/40 uppercase">Requests</p>
                                                <button onClick={() => handleExportRequests('csv')} className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/10 rounded-md">📄 CSV</button>
                                                <button onClick={() => handleExportRequests('json')} className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/10 rounded-md">📋 JSON</button>
                                                <button onClick={() => handleExportRequests('geojson')} className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/10 rounded-md">🗺️ GeoJSON</button>
                                                <div className="border-t border-white/10 my-1" />
                                                <p className="px-3 py-1.5 text-xs font-semibold text-white/40 uppercase">Statistics</p>
                                                <button onClick={() => handleExportStatistics('csv')} className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/10 rounded-md">📊 CSV</button>
                                                <button onClick={() => handleExportStatistics('json')} className="w-full text-left px-3 py-2 text-sm text-white/80 hover:bg-white/10 rounded-md">📈 JSON</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Overview Stats Row */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 sm:p-5">
                                    <div className="text-[10px] sm:text-xs font-medium text-white/70 uppercase tracking-wider">Total Requests</div>
                                    <div className="text-2xl sm:text-3xl font-bold text-white mt-1 sm:mt-2">{advancedStats?.total_requests || 0}</div>
                                    <div className="text-[10px] sm:text-xs text-white/60 mt-1">All time</div>
                                </div>
                                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 sm:p-5">
                                    <div className="text-[10px] sm:text-xs font-medium text-white/70 uppercase tracking-wider">Open</div>
                                    <div className="text-2xl sm:text-3xl font-bold text-amber-300 mt-1 sm:mt-2">{advancedStats?.open_requests || 0}</div>
                                    <div className="text-[10px] sm:text-xs text-white/60 mt-1">Awaiting action</div>
                                </div>
                                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 sm:p-5">
                                    <div className="text-[10px] sm:text-xs font-medium text-white/70 uppercase tracking-wider">In Progress</div>
                                    <div className="text-2xl sm:text-3xl font-bold text-blue-300 mt-1 sm:mt-2">{advancedStats?.in_progress_requests || 0}</div>
                                    <div className="text-[10px] sm:text-xs text-white/60 mt-1">Being worked on</div>
                                </div>
                                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3 sm:p-5">
                                    <div className="text-[10px] sm:text-xs font-medium text-white/70 uppercase tracking-wider">Closed</div>
                                    <div className="text-2xl sm:text-3xl font-bold text-emerald-300 mt-1 sm:mt-2">{advancedStats?.closed_requests || 0}</div>
                                    <div className="text-[10px] sm:text-xs text-white/60 mt-1">{advancedStats?.resolution_rate?.toFixed(0) || 0}% resolution</div>
                                </div>
                            </div>

                            {/* Priority Distribution */}
                            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6">
                                <h2 className="text-lg font-semibold text-white mb-4">Priority Distribution</h2>
                                {(() => {
                                    const highPriority = allRequests.filter(r => {
                                        const p = (r as any).manual_priority_score ?? ((r as any).ai_analysis?.priority_score) ?? 5;
                                        return p >= 8;
                                    }).length;
                                    const mediumPriority = allRequests.filter(r => {
                                        const p = (r as any).manual_priority_score ?? ((r as any).ai_analysis?.priority_score) ?? 5;
                                        return p >= 5 && p < 8;
                                    }).length;
                                    const lowPriority = allRequests.filter(r => {
                                        const p = (r as any).manual_priority_score ?? ((r as any).ai_analysis?.priority_score) ?? 5;
                                        return p < 5;
                                    }).length;
                                    const total = allRequests.length || 1;
                                    return (
                                        <div className="space-y-3">
                                            <div className="flex h-6 rounded-full overflow-hidden bg-white/5">
                                                <div className="bg-red-600 transition-all flex items-center justify-center text-[11px] font-bold text-white" style={{ width: `${(highPriority / total) * 100}%`, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                    {highPriority > 0 && `${Math.round((highPriority / total) * 100)}%`}
                                                </div>
                                                <div className="bg-amber-600 transition-all flex items-center justify-center text-[11px] font-bold text-white" style={{ width: `${(mediumPriority / total) * 100}%`, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                    {mediumPriority > 0 && `${Math.round((mediumPriority / total) * 100)}%`}
                                                </div>
                                                <div className="bg-emerald-600 transition-all flex items-center justify-center text-[11px] font-bold text-white" style={{ width: `${(lowPriority / total) * 100}%`, textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                                                    {lowPriority > 0 && `${Math.round((lowPriority / total) * 100)}%`}
                                                </div>
                                            </div>
                                            <div className="flex flex-col sm:flex-row justify-between text-xs sm:text-sm gap-1">
                                                <span className="text-red-300">● High (8-10): <strong>{highPriority}</strong></span>
                                                <span className="text-amber-300">● Medium (5-7): <strong>{mediumPriority}</strong></span>
                                                <span className="text-emerald-300">● Low (1-4): <strong>{lowPriority}</strong></span>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* KPI Cards */}
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                                <div className="bg-gradient-to-br from-purple-500/20 to-purple-500/5 border border-purple-500/30 rounded-xl p-3 sm:p-5" title="Predicted number of new service requests we expect to receive over the next 7 days, based on recent volume patterns.">
                                    <div className="text-[10px] sm:text-xs font-bold text-purple-300 uppercase tracking-wider">Forecast · Next 7 Days</div>
                                    <div className="text-2xl sm:text-3xl font-bold text-white mt-1 sm:mt-2">{advancedStats?.predictive_insights?.volume_forecast_next_week || 0}</div>
                                    <div className="text-[10px] sm:text-xs text-white/80 mt-1 capitalize">
                                        Expected new tickets · trend <span className="font-semibold">{advancedStats?.predictive_insights?.trend_direction || 'stable'}</span>
                                    </div>
                                    <div className="text-[10px] text-white/50 mt-1.5 leading-snug">Plan crew capacity for the week ahead.</div>
                                </div>
                                <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/30 rounded-xl p-3 sm:p-5" title="Average time from when a request is submitted until it is marked closed. Lower is better.">
                                    <div className="text-[10px] sm:text-xs font-bold text-emerald-300 uppercase tracking-wider">Avg. Time to Resolve</div>
                                    <div className="text-2xl sm:text-3xl font-bold text-white mt-1 sm:mt-2">{advancedStats?.avg_resolution_hours ? `${advancedStats.avg_resolution_hours.toFixed(1)}h` : 'N/A'}</div>
                                    <div className="text-[10px] sm:text-xs text-white/80 mt-1">
                                        <span className="font-semibold">{advancedStats?.resolution_rate?.toFixed(0) || 0}%</span> of all tickets closed
                                    </div>
                                    <div className="text-[10px] text-white/50 mt-1.5 leading-snug">From submission to close · lower is better.</div>
                                </div>
                                <div className="bg-gradient-to-br from-red-500/20 to-red-500/5 border border-red-500/30 rounded-xl p-3 sm:p-5" title="High-priority requests (priority 1-3) that have been open for more than 7 days. These need immediate attention.">
                                    <div className="text-[10px] sm:text-xs font-bold text-red-300 uppercase tracking-wider">Aging · Needs Attention</div>
                                    <div className="text-2xl sm:text-3xl font-bold text-white mt-1 sm:mt-2">{advancedStats?.aging_high_priority_count || 0}</div>
                                    <div className="text-[10px] sm:text-xs text-white/80 mt-1">High-priority tickets open &gt; 7 days</div>
                                    <div className="text-[10px] text-white/50 mt-1.5 leading-snug">Escalate to avoid SLA breaches.</div>
                                </div>
                                <div className="bg-gradient-to-br from-blue-500/20 to-blue-500/5 border border-blue-500/30 rounded-xl p-3 sm:p-5" title="The day of the week and month of the year that historically receive the most service requests.">
                                    <div className="text-[10px] sm:text-xs font-bold text-blue-300 uppercase tracking-wider">Busiest Period</div>
                                    <div className="text-2xl sm:text-3xl font-bold text-white mt-1 sm:mt-2">{advancedStats?.predictive_insights?.seasonal_peak_day || 'N/A'}</div>
                                    <div className="text-[10px] sm:text-xs text-white/80 mt-1">
                                        Peak month: <span className="font-semibold">{advancedStats?.predictive_insights?.seasonal_peak_month || 'N/A'}</span>
                                    </div>
                                    <div className="text-[10px] text-white/50 mt-1.5 leading-snug">Staff up on these days &amp; months.</div>
                                </div>
                            </div>

                            {/* Two-Column: Categories + Weekly Trend */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                                {/* Requests by Category — Horizontal Bars */}
                                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">Requests by Category</h3>
                                    <div className="space-y-3">
                                        {(() => {
                                            const entries = Object.entries(advancedStats?.requests_by_category || {})
                                                .sort(([, a], [, b]) => (b as number) - (a as number));
                                            const maxCount = entries.length > 0 ? (entries[0][1] as number) : 1;
                                            return entries.map(([category, count]) => (
                                                <div key={category}>
                                                    <div className="flex justify-between text-sm mb-1">
                                                        <span className="text-white/80 truncate mr-2">{category}</span>
                                                        <span className="text-white/60 font-medium flex-shrink-0">{count as number}</span>
                                                    </div>
                                                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all"
                                                            style={{ width: `${((count as number) / maxCount) * 100}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            ));
                                        })()}
                                    </div>
                                </div>

                                {/* Weekly Trend Chart */}
                                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 sm:p-6">
                                    <h3 className="text-lg font-semibold text-white mb-4">Weekly Trend</h3>
                                    <div className="h-48 sm:h-64">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={advancedStats?.weekly_trend || []}>
                                                <XAxis dataKey="period" stroke="rgba(255,255,255,0.3)" style={{ fontSize: '11px' }} tick={{ fill: 'rgba(255,255,255,0.5)' }} />
                                                <YAxis stroke="rgba(255,255,255,0.3)" style={{ fontSize: '11px' }} tick={{ fill: 'rgba(255,255,255,0.5)' }} />
                                                <Tooltip contentStyle={{ backgroundColor: 'rgba(17,24,39,0.95)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }} />
                                                <Area type="monotone" dataKey="total" stroke="#8b5cf6" fill="url(#purpleGradient)" fillOpacity={0.4} strokeWidth={2} />
                                                <defs>
                                                    <linearGradient id="purpleGradient" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="0%" stopColor="#8b5cf6" stopOpacity={0.6} />
                                                        <stop offset="100%" stopColor="#8b5cf6" stopOpacity={0.05} />
                                                    </linearGradient>
                                                </defs>
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>

                            {/* Staff Activity — Executive Grid */}
                            <div className="bg-gradient-to-br from-slate-900/60 to-slate-800/40 backdrop-blur-sm border border-white/10 rounded-2xl p-4 sm:p-6 shadow-xl">
                                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-5 gap-2">
                                    <div>
                                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                            <Award className="w-5 h-5 text-amber-400" />
                                            Staff Activity Summary
                                        </h3>
                                        <p className="text-xs text-white/50 mt-1">Top performers across resolution, response, and engagement</p>
                                    </div>
                                    {staffActivity?.totals && (
                                        <div className="flex gap-4 text-xs">
                                            <div className="text-right">
                                                <div className="text-white/40 uppercase tracking-wider text-[10px]">Status Updates</div>
                                                <div className="text-lg font-bold text-emerald-300">{staffActivity.totals.status_changes.toLocaleString()}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-white/40 uppercase tracking-wider text-[10px]">Comments</div>
                                                <div className="text-lg font-bold text-blue-300">{staffActivity.totals.comments.toLocaleString()}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
                                    {/* Top Resolvers */}
                                    <StaffLeaderboardCard
                                        title="Top Resolvers"
                                        subtitle="Tickets closed"
                                        icon={Award}
                                        accent="emerald"
                                        data={staffActivity?.top_resolvers || advancedStats?.top_staff_by_resolutions || {}}
                                        valueLabel=""
                                    />

                                    {/* Top Status Changers */}
                                    <StaffLeaderboardCard
                                        title="Top Status Changers"
                                        subtitle="Workflow updates"
                                        icon={RefreshCw}
                                        accent="indigo"
                                        data={staffActivity?.top_status_changers || {}}
                                        valueLabel=""
                                    />

                                    {/* Top Commenters */}
                                    <StaffLeaderboardCard
                                        title="Top Commenters"
                                        subtitle="Resident engagement"
                                        icon={MessageSquare}
                                        accent="blue"
                                        data={staffActivity?.top_commenters || {}}
                                        valueLabel=""
                                    />

                                    {/* Current Workload */}
                                    <StaffLeaderboardCard
                                        title="Active Workload"
                                        subtitle="Open assignments"
                                        icon={Briefcase}
                                        accent="amber"
                                        data={advancedStats?.workload_by_staff || {}}
                                        valueLabel="active"
                                    />

                                    {/* Top Attenders (assigners) */}
                                    {staffActivity?.top_assigners && Object.keys(staffActivity.top_assigners).length > 0 && (
                                        <StaffLeaderboardCard
                                            title="Top Attenders"
                                            subtitle="Assignments made"
                                            icon={UserCheck}
                                            accent="purple"
                                            data={staffActivity.top_assigners}
                                            valueLabel=""
                                        />
                                    )}

                                    {/* Top Assignees (most-assigned) */}
                                    {staffActivity?.top_assignees && Object.keys(staffActivity.top_assignees).length > 0 && (
                                        <StaffLeaderboardCard
                                            title="Most Assigned"
                                            subtitle="Total tickets received"
                                            icon={TrendingUp}
                                            accent="rose"
                                            data={staffActivity.top_assignees}
                                            valueLabel=""
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Spatial Bias Heatmap — Full Width */}
                            <SpatialBiasHeatmap
                                heatmapData={heatmapData}
                                hotspots={advancedStats?.hotspots || []}
                                defaultCenter={mapsConfig?.default_center || advancedStats?.geographic_center || undefined}
                            />
                        </div>
                    </div>
                )} {/* This closes the conditional for currentView === 'statistics' || currentView === 'dashboard' */}

                {/* ── Reports ── */}
                {currentView === 'reports' && user?.role !== 'staff' && (
                    <ReportsPanel
                        onExportRequests={(fmt, params) => handleExportRequests(fmt, params)}
                        onExportStatistics={(fmt, params) => handleExportStatistics(fmt, params)}
                        onPreviewRequests={(params) => api.previewRequests(params)}
                        onPreviewStatistics={(params) => api.previewStatistics(params)}
                        services={services}
                        departments={departments}
                    />
                )}

                {/* ── Deleted Tickets ── */}
                {currentView === 'deleted_tickets' && (user?.role === 'admin' || user?.role === 'staff_admin' || user?.role === 'org_admin') && (() => {
                    const filteredDeleted = deletedRequests.filter(r => {
                        const term = deletedSearch.toLowerCase();
                        const matchSearch = !term ||
                            r.service_request_id.toLowerCase().includes(term) ||
                            (r.service_name || '').toLowerCase().includes(term) ||
                            (r.address || '').toLowerCase().includes(term) ||
                            ((r as any).deleted_by || '').toLowerCase().includes(term) ||
                            ((r as any).delete_justification || '').toLowerCase().includes(term);
                        const matchCategory = !deletedFilterCategory || r.service_name === deletedFilterCategory;
                        const matchDeletedBy = !deletedFilterDeletedBy || (r as any).deleted_by === deletedFilterDeletedBy;
                        return matchSearch && matchCategory && matchDeletedBy;
                    });
                    const totalDeletedPages = Math.max(1, Math.ceil(filteredDeleted.length / deletedPageSize));
                    const pagedDeleted = filteredDeleted.slice((deletedPage - 1) * deletedPageSize, deletedPage * deletedPageSize);
                    const deletedCategories = [...new Set(deletedRequests.map(r => r.service_name).filter(Boolean))] as string[];
                    const deletedByUsers = [...new Set(deletedRequests.map(r => (r as any).deleted_by).filter(Boolean))] as string[];
                    return (
                    <div className="flex-1 overflow-auto p-4 lg:p-6">
                        <div className="max-w-5xl mx-auto space-y-4">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <div>
                                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                        <Trash2 className="w-5 h-5 text-red-400" />
                                        Deleted Tickets
                                        {filteredDeleted.length > 0 && <span className="ml-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 text-xs border border-red-500/30">{filteredDeleted.length}</span>}
                                    </h2>
                                    <p className="text-white/40 text-sm mt-0.5">Soft-deleted service requests. Restore to make them active again.</p>
                                </div>
                                <button
                                    onClick={() => { loadDeletedTickets(); setDeletedPage(1); }}
                                    disabled={deletedLoading}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white text-sm transition-all disabled:opacity-50"
                                >
                                    <RefreshCw className={`w-4 h-4 ${deletedLoading ? 'animate-spin' : ''}`} />
                                    Refresh
                                </button>
                            </div>

                            {/* Search + Filters */}
                            <div className="flex flex-col sm:flex-row gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                                    <input
                                        type="text"
                                        placeholder="Search by ID, category, address, deleted by…"
                                        value={deletedSearch}
                                        onChange={e => { setDeletedSearch(e.target.value); setDeletedPage(1); }}
                                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-primary-500/50"
                                    />
                                </div>
                                <select
                                    value={deletedFilterCategory}
                                    onChange={e => { setDeletedFilterCategory(e.target.value); setDeletedPage(1); }}
                                    className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-primary-500/50"
                                >
                                    <option value="">All Categories</option>
                                    {deletedCategories.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                {deletedByUsers.length > 0 && (
                                    <select
                                        value={deletedFilterDeletedBy}
                                        onChange={e => { setDeletedFilterDeletedBy(e.target.value); setDeletedPage(1); }}
                                        className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white focus:outline-none focus:border-primary-500/50"
                                    >
                                        <option value="">All Staff</option>
                                        {deletedByUsers.map(u => <option key={u} value={u}>{u}</option>)}
                                    </select>
                                )}
                                {(deletedSearch || deletedFilterCategory || deletedFilterDeletedBy) && (
                                    <button
                                        onClick={() => { setDeletedSearch(''); setDeletedFilterCategory(''); setDeletedFilterDeletedBy(''); setDeletedPage(1); }}
                                        className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/50 hover:text-white text-sm transition-all"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>

                            {/* Error */}
                            {deletedError && (
                                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
                                    {deletedError}
                                </div>
                            )}

                            {/* Loading */}
                            {deletedLoading && (
                                <div className="flex items-center justify-center py-16">
                                    <Loader2 className="w-8 h-8 animate-spin text-white/40" />
                                </div>
                            )}

                            {/* Empty */}
                            {!deletedLoading && !deletedError && filteredDeleted.length === 0 && (
                                <div className="flex flex-col items-center justify-center py-16 text-white/30">
                                    <Trash2 className="w-12 h-12 mb-3 opacity-30" />
                                    <p className="text-sm">{deletedRequests.length === 0 ? 'No deleted tickets found' : 'No tickets match your filters'}</p>
                                </div>
                            )}

                            {/* List */}
                            {!deletedLoading && pagedDeleted.length > 0 && (
                                <div className="space-y-3">
                                    {pagedDeleted.map((r) => (
                                        <div
                                            key={r.service_request_id}
                                            className="bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                                        >
                                            {/* Ticket info */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                    <span className="font-mono text-xs text-white/40">{r.service_request_id}</span>
                                                    <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 text-xs border border-red-500/30">Deleted</span>
                                                    {(r as any).deleted_by && (
                                                        <span className="text-white/60 text-xs">by {(r as any).deleted_by}</span>
                                                    )}
                                                </div>
                                                <p className="font-medium text-white truncate">{r.service_name}</p>
                                                {r.address && <p className="text-white/40 text-sm truncate">{r.address}</p>}
                                                {(r as any).delete_justification && (
                                                    <p className="text-amber-300/70 text-xs mt-1 italic">
                                                        Reason: {(r as any).delete_justification}
                                                    </p>
                                                )}
                                                <p className="text-white/60 text-xs mt-1">
                                                    Submitted {new Date(r.requested_datetime).toLocaleDateString()}
                                                    {(r as any).deleted_at && ` · Deleted ${new Date((r as any).deleted_at).toLocaleDateString()}`}
                                                </p>
                                            </div>

                                            {/* Restore button */}
                                            <button
                                                onClick={() => handleRestoreTicket(r.service_request_id)}
                                                disabled={restoringId === r.service_request_id}
                                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-300 text-sm font-medium transition-all disabled:opacity-50 flex-shrink-0"
                                            >
                                                {restoringId === r.service_request_id
                                                    ? <Loader2 className="w-4 h-4 animate-spin" />
                                                    : <RefreshCw className="w-4 h-4" />
                                                }
                                                {restoringId === r.service_request_id ? 'Restoring…' : 'Restore'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Pagination */}
                            {!deletedLoading && totalDeletedPages > 1 && (
                                <div className="flex items-center justify-between pt-2">
                                    <p className="text-white/30 text-xs">
                                        Showing {(deletedPage - 1) * deletedPageSize + 1}–{Math.min(deletedPage * deletedPageSize, filteredDeleted.length)} of {filteredDeleted.length}
                                    </p>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => setDeletedPage(p => Math.max(1, p - 1))}
                                            disabled={deletedPage === 1}
                                            className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white text-sm disabled:opacity-30 transition-all"
                                        >
                                            ‹ Prev
                                        </button>
                                        {Array.from({ length: totalDeletedPages }, (_, i) => i + 1)
                                            .filter(p => p === 1 || p === totalDeletedPages || Math.abs(p - deletedPage) <= 1)
                                            .reduce<(number | string)[]>((acc, p, idx, arr) => {
                                                if (idx > 0 && (p as number) - (arr[idx - 1] as number) > 1) acc.push('…');
                                                acc.push(p);
                                                return acc;
                                            }, [])
                                            .map((p, idx) => p === '…' ? (
                                                <span key={`ellipsis-${idx}`} className="px-2 text-white/30 text-sm">…</span>
                                            ) : (
                                                <button
                                                    key={p}
                                                    onClick={() => setDeletedPage(p as number)}
                                                    className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${
                                                        deletedPage === p
                                                            ? 'bg-primary-500/20 border-primary-500/50 text-white'
                                                            : 'bg-white/5 hover:bg-white/10 border-white/10 text-white/60 hover:text-white'
                                                    }`}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                        <button
                                            onClick={() => setDeletedPage(p => Math.min(totalDeletedPages, p + 1))}
                                            disabled={deletedPage === totalDeletedPages}
                                            className="px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white text-sm disabled:opacity-30 transition-all"
                                        >
                                            Next ›
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    );
                })()}

                {/* ── Report a Problem ── */}
                {currentView === 'report' && (
                    <div className="flex-1 overflow-auto p-4 lg:p-8">
                        <div className="max-w-2xl mx-auto">
                            {/* Page header */}
                            <div className="mb-5">
                                <h2 className="text-2xl font-bold text-white mb-1">Report a Problem</h2>
                                <p className="text-white/50 text-sm">Submit or manage your reported issues.</p>
                            </div>

                            {/* Tabs */}
                            <div className="flex gap-1 mb-6 bg-white/5 border border-white/10 rounded-xl p-1">
                                <button
                                    onClick={() => { setReportTab('submit'); setIntakeSuccess(null); setIntakeSubmitError(null); }}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${reportTab === 'submit' ? 'bg-primary-500/20 text-white border border-primary-500/30' : 'text-white/50 hover:text-white'}`}
                                >
                                    <PlusCircle className="w-4 h-4" />
                                    New Report
                                </button>
                                <button
                                    onClick={() => setReportTab('my')}
                                    className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all ${reportTab === 'my' ? 'bg-primary-500/20 text-white border border-primary-500/30' : 'text-white/50 hover:text-white'}`}
                                >
                                    <ClipboardList className="w-4 h-4" />
                                    My Submissions
                                </button>
                            </div>

                            {/* ── New Report Form ── */}
                            {reportTab === 'submit' && (
                                <>
                                {intakeSuccess ? (
                                    <div className="flex flex-col items-center justify-center py-20 gap-5">
                                        <div className="w-20 h-20 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                                            <CheckCircle className="w-10 h-10 text-emerald-400" />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-white font-semibold text-lg">{intakeSuccess}</p>
                                            <p className="text-white/50 text-sm mt-1">AI analysis will begin shortly.</p>
                                        </div>
                                        <div className="flex gap-3">
                                            <Button onClick={() => { setIntakeSuccess(null); setIntakeData({ service_code: '', description: '', address: '', lat: null, long: null, first_name: '', last_name: '', phone: '', media_urls: [], source: 'staff_observation', hidden_from_public: true }); }}>
                                                Submit Another
                                            </Button>
                                            <Button variant="ghost" onClick={() => setReportTab('my')}>
                                                View My Submissions
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <form onSubmit={handleCreateIntake} className="space-y-5">
                                        <div className="flex items-start gap-2.5 bg-primary-500/10 border border-primary-500/20 rounded-xl px-4 py-3 text-sm text-primary-300">
                                            <ClipboardList className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                            <span>Reporting as <strong>{user?.full_name || user?.username}</strong> · Your staff account is verified — no SMS required</span>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <Select
                                                label="Problem Category *"
                                                options={[
                                                    { value: '', label: 'Select a category…' },
                                                    ...services.map((s) => ({ value: s.service_code, label: s.service_name })),
                                                ]}
                                                value={intakeData.service_code}
                                                onChange={(e) => setIntakeData((prev) => ({ ...prev, service_code: e.target.value }))}
                                                required
                                            />
                                            <Select
                                                label="How was this reported?"
                                                options={[
                                                    { value: 'staff_observation', label: 'Staff Observation (I saw it)' },
                                                    { value: 'phone', label: 'Phone Call from Resident' },
                                                    { value: 'walk_in', label: 'Walk-In / In-Person' },
                                                    { value: 'email', label: 'Email from Resident' },
                                                ]}
                                                value={intakeData.source}
                                                onChange={(e) => setIntakeData((prev) => ({ ...prev, source: e.target.value as typeof intakeData.source }))}
                                            />
                                        </div>

                                        <Textarea
                                            label="Description *"
                                            placeholder="Describe the problem in detail — what you observed, where exactly, severity…"
                                            value={intakeData.description}
                                            onChange={(e) => setIntakeData((prev) => ({ ...prev, description: e.target.value }))}
                                            required
                                            rows={4}
                                        />

                                        <div>
                                            <label className="block text-sm font-medium text-white/70 mb-1.5">Location / Address</label>
                                            <GoogleMapsLocationPicker
                                                defaultCenter={mapsConfig?.default_center || { lat: 4.175, lng: 73.509 }}
                                                defaultZoom={14}
                                                townshipBoundary={mapsConfig?.township_boundary}
                                                value={{ address: intakeData.address, lat: intakeData.lat, lng: intakeData.long }}
                                                onChange={(loc) => { setIntakeData((prev) => ({ ...prev, address: loc.address, lat: loc.lat ?? null, long: loc.lng ?? null })); if (loc.lat === null) setIntakeOutsideBoundary(false); }}
                                                onOutOfBounds={() => setIntakeOutsideBoundary(true)}
                                                onBoundaryChange={(isOutside) => setIntakeOutsideBoundary(isOutside)}
                                                placeholder="Search address or tap map to pin location…"
                                                className="rounded-xl overflow-hidden"
                                            />
                                            {intakeData.lat && intakeData.long && (
                                                <p className="text-xs text-primary-400 mt-1.5">📍 {intakeData.lat.toFixed(5)}, {intakeData.long.toFixed(5)}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-white/70 mb-1.5">
                                                Photos <span className="text-white/30 font-normal">(up to 3)</span>
                                            </label>
                                            <div className="flex flex-wrap gap-3">
                                                {intakeData.media_urls.map((url, idx) => (
                                                    <div key={url} className="relative w-24 h-24 rounded-xl overflow-hidden border border-white/10 group">
                                                        <img src={url} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                                                        <button type="button" onClick={() => setIntakeData((prev) => ({ ...prev, media_urls: prev.media_urls.filter((_, i) => i !== idx) }))} className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" aria-label="Remove photo">
                                                            <X className="w-5 h-5 text-white" />
                                                        </button>
                                                    </div>
                                                ))}
                                                {intakeData.media_urls.length < 3 && (
                                                    <label className={`w-24 h-24 rounded-xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-primary-500/50 hover:bg-primary-500/5 transition-all ${photoUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                                                        {photoUploading ? <div className="w-5 h-5 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" /> : <><Camera className="w-5 h-5 text-white/40" /><span className="text-[10px] text-white/40">Add photo</span></>}
                                                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={async (e) => {
                                                            const file = e.target.files?.[0]; if (!file) return;
                                                            setPhotoUploading(true);
                                                            try { const { url } = await api.uploadImage(file); setIntakeData((prev) => ({ ...prev, media_urls: [...prev.media_urls, url] })); }
                                                            catch (err: any) { setIntakeSubmitError('Photo upload failed: ' + (err?.message || 'Unknown error')); }
                                                            finally { setPhotoUploading(false); e.target.value = ''; }
                                                        }} />
                                                    </label>
                                                )}
                                            </div>
                                        </div>

                                        {intakeData.source !== 'staff_observation' && (
                                            <div className="grid grid-cols-2 gap-3">
                                                <Input label="Resident First Name" placeholder="Optional" value={intakeData.first_name} onChange={(e) => setIntakeData((prev) => ({ ...prev, first_name: e.target.value }))} />
                                                <Input label="Resident Last Name" placeholder="Optional" value={intakeData.last_name} onChange={(e) => setIntakeData((prev) => ({ ...prev, last_name: e.target.value }))} />
                                                <Input label="Phone" placeholder="Optional" value={intakeData.phone} onChange={(e) => setIntakeData((prev) => ({ ...prev, phone: e.target.value }))} className="col-span-2" />
                                            </div>
                                        )}

                                        {intakeSubmitError && (
                                            <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
                                                <AlertCircle className="w-4 h-4 flex-shrink-0" />{intakeSubmitError}
                                            </div>
                                        )}

                                        {/* Public visibility toggle */}
                                        <div className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${intakeData.hidden_from_public ? 'bg-slate-800/60 border-white/10' : 'bg-emerald-500/10 border-emerald-500/30'}`}>
                                            <div>
                                                <p className="text-sm font-medium text-white">
                                                    {intakeData.hidden_from_public ? '🔒 Hidden from public' : '🌐 Visible to public'}
                                                </p>
                                                <p className="text-xs text-white/50 mt-0.5">
                                                    {intakeData.hidden_from_public
                                                        ? 'Only staff can see this report'
                                                        : 'Residents can see this on the public tracker'}
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setIntakeData(prev => ({ ...prev, hidden_from_public: !prev.hidden_from_public }))}
                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                                                    intakeData.hidden_from_public ? 'bg-white/20' : 'bg-emerald-500'
                                                }`}
                                                role="switch"
                                                aria-checked={!intakeData.hidden_from_public}
                                            >
                                                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                                                    intakeData.hidden_from_public ? 'translate-x-0' : 'translate-x-5'
                                                }`} />
                                            </button>
                                        </div>

                                        <div className="flex justify-end gap-3 pt-2">
                                            <Button variant="ghost" type="button" onClick={() => { setCurrentView('dashboard'); setIntakeSubmitError(null); }}>Cancel</Button>
                                            <Button type="submit" disabled={intakeSubmitting || !intakeData.service_code || intakeData.description.length < 10 || photoUploading || intakeOutsideBoundary} title={intakeOutsideBoundary ? 'Location is outside the service area' : undefined}>
                                                {intakeSubmitting ? 'Submitting…' : 'Submit Report'}
                                            </Button>
                                        </div>
                                        {intakeOutsideBoundary && (
                                            <p className="text-right text-xs text-red-400 mt-1">⚠ Move the pin inside the service boundary to submit.</p>
                                        )}
                                    </form>
                                )}
                                </>
                            )}

                            {/* ── My Submissions ── */}
                            {reportTab === 'my' && (
                                <div className="space-y-3">
                                    {mySubmissionsLoading ? (
                                        <div className="flex items-center justify-center py-16 text-white/30">
                                            <div className="w-6 h-6 border-2 border-primary-400 border-t-transparent rounded-full animate-spin mr-3" />
                                            Loading…
                                        </div>
                                    ) : mySubmissions.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-16 gap-3 text-white/30">
                                            <ClipboardList className="w-10 h-10" />
                                            <p className="text-sm">You haven't submitted any reports yet.</p>
                                            <Button size="sm" onClick={() => setReportTab('submit')}>Submit Your First Report</Button>
                                        </div>
                                    ) : (() => {
                                        const filtered = mySubmissions.filter(r => {
                                            if (subStatusFilter && r.status !== subStatusFilter) return false;
                                            if (subServiceFilter && r.service_name !== subServiceFilter) return false;
                                            if (subDateFrom) {
                                                const from = new Date(subDateFrom);
                                                from.setHours(0, 0, 0, 0);
                                                if (new Date(r.requested_datetime!) < from) return false;
                                            }
                                            if (subDateTo) {
                                                const to = new Date(subDateTo);
                                                to.setHours(23, 59, 59, 999);
                                                if (new Date(r.requested_datetime!) > to) return false;
                                            }
                                            if (subSearch) {
                                                const q = subSearch.toLowerCase();
                                                return (
                                                    r.service_request_id.toLowerCase().includes(q) ||
                                                    (r.service_name || '').toLowerCase().includes(q) ||
                                                    (r.description || '').toLowerCase().includes(q) ||
                                                    (r.address || '').toLowerCase().includes(q)
                                                );
                                            }
                                            return true;
                                        });

                                        // Unique service categories from all submissions
                                        const serviceOptions = Array.from(new Set(mySubmissions.map(r => r.service_name).filter(Boolean))).sort();

                                        const sorted = [...filtered].sort((a, b) => {
                                            if (subSort === 'newest') return new Date(b.requested_datetime!).getTime() - new Date(a.requested_datetime!).getTime();
                                            if (subSort === 'oldest') return new Date(a.requested_datetime!).getTime() - new Date(b.requested_datetime!).getTime();
                                            if (subSort === 'updated') return new Date(b.updated_datetime || b.requested_datetime!).getTime() - new Date(a.updated_datetime || a.requested_datetime!).getTime();
                                            if (subSort === 'service') return (a.service_name || '').localeCompare(b.service_name || '');
                                            return a.status.localeCompare(b.status);
                                        });

                                        const totalPages = Math.max(1, Math.ceil(sorted.length / subPageSize));
                                        const page = Math.min(subPage, totalPages);
                                        const paginated = sorted.slice((page - 1) * subPageSize, page * subPageSize);

                                        const fmt = (dt: string) => {
                                            const d = new Date(dt);
                                            return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                                        };

                                        return (
                                            <>
                                                {/* Search */}
                                                <div className="relative">
                                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search by ID, service, description, address…"
                                                        value={subSearch}
                                                        onChange={e => { setSubSearch(e.target.value); setSubPage(1); }}
                                                        className="w-full pl-8 pr-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/20"
                                                    />
                                                    {subSearch && (
                                                        <button onClick={() => { setSubSearch(''); setSubPage(1); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white">
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Date range */}
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="text-[10px] text-white/40 mb-1 block uppercase tracking-wide">From</label>
                                                        <input
                                                            type="date"
                                                            value={subDateFrom}
                                                            onChange={e => { setSubDateFrom(e.target.value); setSubPage(1); }}
                                                            className="w-full px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-primary-500/50 [color-scheme:dark]"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] text-white/40 mb-1 block uppercase tracking-wide">To</label>
                                                        <input
                                                            type="date"
                                                            value={subDateTo}
                                                            onChange={e => { setSubDateTo(e.target.value); setSubPage(1); }}
                                                            className="w-full px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs focus:outline-none focus:border-primary-500/50 [color-scheme:dark]"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Filters row */}
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-xs text-white/40 mr-auto">
                                                        {filtered.length} of {mySubmissions.length} submission{mySubmissions.length !== 1 ? 's' : ''}
                                                    </span>
                                                    {/* Category */}
                                                    <select
                                                        value={subServiceFilter}
                                                        onChange={e => { setSubServiceFilter(e.target.value); setSubPage(1); }}
                                                        className="text-xs px-2 py-1.5 rounded-lg bg-white/8 border border-white/10 text-white/70 focus:outline-none focus:border-primary-500/50"
                                                    >
                                                        <option value="">All categories</option>
                                                        {serviceOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                                    </select>
                                                    {/* Status */}
                                                    <select
                                                        value={subStatusFilter}
                                                        onChange={e => { setSubStatusFilter(e.target.value); setSubPage(1); }}
                                                        className="text-xs px-2 py-1.5 rounded-lg bg-white/8 border border-white/10 text-white/70 focus:outline-none focus:border-primary-500/50"
                                                    >
                                                        <option value="">All statuses</option>
                                                        <option value="open">Open</option>
                                                        <option value="in_progress">In Progress</option>
                                                        <option value="closed">Closed</option>
                                                    </select>
                                                    {/* Sort */}
                                                    <select
                                                        value={subSort}
                                                        onChange={e => { setSubSort(e.target.value as any); setSubPage(1); }}
                                                        className="text-xs px-2 py-1.5 rounded-lg bg-white/8 border border-white/10 text-white/70 focus:outline-none focus:border-primary-500/50"
                                                    >
                                                        <option value="newest">Newest first</option>
                                                        <option value="oldest">Oldest first</option>
                                                        <option value="updated">Recently updated</option>
                                                        <option value="service">By category</option>
                                                        <option value="status">By status</option>
                                                    </select>
                                                    {/* Page size */}
                                                    <select
                                                        value={subPageSize}
                                                        onChange={e => { setSubPageSize(Number(e.target.value)); setSubPage(1); }}
                                                        className="text-xs px-2 py-1.5 rounded-lg bg-white/8 border border-white/10 text-white/70 focus:outline-none focus:border-primary-500/50"
                                                    >
                                                        <option value={10}>10 / page</option>
                                                        <option value={25}>25 / page</option>
                                                        <option value={50}>50 / page</option>
                                                    </select>
                                                    {/* Clear all filters */}
                                                    {(subSearch || subStatusFilter || subServiceFilter || subDateFrom || subDateTo) && (
                                                        <button
                                                            onClick={() => { setSubSearch(''); setSubStatusFilter(''); setSubServiceFilter(''); setSubDateFrom(''); setSubDateTo(''); setSubPage(1); }}
                                                            className="text-xs px-2 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors"
                                                        >
                                                            Clear filters
                                                        </button>
                                                    )}
                                                </div>

                                                {filtered.length === 0 ? (
                                                    <div className="flex flex-col items-center justify-center py-10 gap-2 text-white/30">
                                                        <Search className="w-8 h-8" />
                                                        <p className="text-sm">No submissions match your filters.</p>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {paginated.map((req) => (
                                                            <div
                                                                key={req.service_request_id}
                                                                className="bg-white/5 border border-white/10 rounded-xl p-4 hover:bg-white/8 hover:border-white/20 transition-all cursor-pointer group"
                                                                onClick={() => { loadRequestDetail(req.service_request_id); setCurrentView('active'); }}
                                                            >
                                                                <div className="flex items-start justify-between gap-3">
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                                            <span className="text-xs font-mono text-white/40">{req.service_request_id}</span>
                                                                            <StatusBadge status={req.status} />
                                                                        </div>
                                                                        <p className="text-sm font-semibold text-white truncate">{req.service_name}</p>
                                                                        {req.description && <p className="text-xs text-white/50 mt-0.5 line-clamp-2">{req.description}</p>}
                                                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                                                                            {req.address && (
                                                                                <span className="text-xs text-white/35 flex items-center gap-1">
                                                                                    <MapPin className="w-3 h-3 flex-shrink-0" />{req.address}
                                                                                </span>
                                                                            )}
                                                                            <span className="text-xs text-white/35 flex items-center gap-1">
                                                                                <Calendar className="w-3 h-3 flex-shrink-0" />
                                                                                Submitted: {fmt(req.requested_datetime!)}
                                                                            </span>
                                                                            {req.updated_datetime && req.updated_datetime !== req.requested_datetime && (
                                                                                <span className="text-xs text-white/35 flex items-center gap-1">
                                                                                    <RefreshCw className="w-3 h-3 flex-shrink-0" />
                                                                                    Updated: {fmt(req.updated_datetime)}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                    <ExternalLink className="w-4 h-4 text-white/20 group-hover:text-primary-400 transition-colors flex-shrink-0 mt-0.5" />
                                                                </div>
                                                                {(req as any).media_urls?.length > 0 && (
                                                                    <div className="flex gap-2 mt-3">
                                                                        {((req as any).media_urls as string[]).slice(0, 4).map((url: string, i: number) => (
                                                                            <img key={i} src={url} alt="" className="w-12 h-12 rounded-lg object-cover border border-white/10" />
                                                                        ))}
                                                                        {(req as any).media_urls.length > 4 && (
                                                                            <div className="w-12 h-12 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center text-xs text-white/50">
                                                                                +{(req as any).media_urls.length - 4}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}

                                                        {/* Pagination */}
                                                        {totalPages > 1 && (
                                                            <div className="flex items-center justify-between pt-1">
                                                                <button
                                                                    onClick={() => setSubPage(p => Math.max(1, p - 1))}
                                                                    disabled={page === 1}
                                                                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/8 border border-white/10 text-xs text-white/60 hover:text-white hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                                >
                                                                    <ChevronLeft className="w-3.5 h-3.5" /> Prev
                                                                </button>
                                                                <div className="flex items-center gap-1">
                                                                    {Array.from({ length: totalPages }, (_, i) => i + 1).filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1).reduce((acc: (number | '…')[], p, idx, arr) => {
                                                                        if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('…');
                                                                        acc.push(p);
                                                                        return acc;
                                                                    }, []).map((p, i) => p === '…' ? (
                                                                        <span key={`e${i}`} className="text-xs text-white/30 px-1">…</span>
                                                                    ) : (
                                                                        <button key={p} onClick={() => setSubPage(p as number)} className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${page === p ? 'bg-primary-500 text-white' : 'bg-white/8 border border-white/10 text-white/60 hover:text-white hover:bg-white/15'}`}>{p}</button>
                                                                    ))}
                                                                </div>
                                                                <button
                                                                    onClick={() => setSubPage(p => Math.min(totalPages, p + 1))}
                                                                    disabled={page === totalPages}
                                                                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/8 border border-white/10 text-xs text-white/60 hover:text-white hover:bg-white/15 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                                                >
                                                                    Next <ChevronDown className="w-3.5 h-3.5 -rotate-90" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* AI Analytics Chat — Floating Popup (matches marketing site design) */}
                <AnimatePresence>
                    {chatOpen && (
                        <>
                            {/* Subtle backdrop — click to close */}
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 z-[190]"
                                onClick={() => setChatOpen(false)}
                            />
                            {/* Floating popup card */}
                            <motion.div
                                initial={{ opacity: 0, y: 16, scale: 0.97 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: 16, scale: 0.97 }}
                                transition={{ duration: 0.2, ease: 'easeOut' }}
                                className="fixed z-[210] flex flex-col overflow-hidden"
                                style={{
                                    bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))',
                                    right: '1.25rem',
                                    left: 'auto',
                                    width: 'min(380px, calc(100vw - 2rem))',
                                    maxHeight: 'min(540px, 70vh)',
                                    background: 'rgba(21, 25, 41, 0.97)',
                                    backdropFilter: 'blur(24px)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    borderRadius: '18px',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.5), 0 0 40px rgba(16,185,129,0.1)',
                                }}
                            >
                                {/* Header */}
                                <div className="flex items-center justify-between px-5 py-3.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                    <h3 className="text-[0.92rem] font-bold flex items-center gap-2">
                                        <span className="text-white">✦</span>
                                        <span style={{ background: 'linear-gradient(135deg, #a5b4fc, #22d3a5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Analytics Advisor</span>
                                    </h3>
                                    <div className="flex items-center gap-1.5">
                                        {chatMessages.length > 0 && (
                                            <button
                                                onClick={() => { setChatMessages([]); setChatInput(''); }}
                                                className="flex items-center justify-center transition-colors"
                                                title="Clear conversation"
                                                style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '8px', width: '30px', height: '30px', cursor: 'pointer' }}
                                            >
                                                <Trash2 className="w-3.5 h-3.5 text-white/40 hover:text-red-400" />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setChatOpen(false)}
                                            className="flex items-center justify-center transition-colors"
                                            style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: '8px', width: '30px', height: '30px', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>

                                {/* Messages */}
                                <div
                                    className="flex-1 overflow-y-auto flex flex-col gap-3 min-h-[200px]"
                                    style={{ padding: '1rem 1.25rem', maxHeight: '340px', scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent' }}
                                >
                                    {chatMessages.length === 0 && !chatLoading && (
                                        <div
                                            className="text-[0.82rem] leading-relaxed"
                                            style={{ 
                                                maxWidth: '90%', padding: '0.65rem 0.9rem', borderRadius: '14px', borderBottomLeftRadius: '4px',
                                                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.8)', alignSelf: 'flex-start'
                                            }}
                                        >
                                            👋 Hi! I'm the AI Analytics Advisor. I can help you understand your data — trends, equity metrics, response times, sentiment, and more. What would you like to know?
                                        </div>
                                    )}

                                    {chatMessages.map((msg, i) => (
                                        <div
                                            key={i}
                                            className="text-[0.82rem] leading-relaxed"
                                            style={{
                                                maxWidth: '90%',
                                                padding: '0.65rem 0.9rem',
                                                borderRadius: '14px',
                                                wordBreak: 'break-word',
                                                alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                                ...(msg.role === 'user' ? {
                                                    background: 'rgba(16,185,129,0.2)',
                                                    border: '1px solid rgba(16,185,129,0.25)',
                                                    color: '#a7f3d0',
                                                    borderBottomRightRadius: '4px',
                                                } : {
                                                    background: 'rgba(255,255,255,0.06)',
                                                    border: '1px solid rgba(255,255,255,0.08)',
                                                    color: 'rgba(255,255,255,0.8)',
                                                    borderBottomLeftRadius: '4px',
                                                }),
                                            }}
                                        >
                                            {msg.role === 'assistant' ? (
                                                <div
                                                    dangerouslySetInnerHTML={{
                                                        __html: (() => {
                                                            let html = msg.content;
                                                            html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                                                            html = html.replace(/^### (.+)$/gm, '<strong style="display:block;margin:.5rem 0 .2rem">$1</strong>');
                                                            html = html.replace(/^## (.+)$/gm, '<strong style="display:block;margin:.6rem 0 .2rem;font-size:.9rem">$1</strong>');
                                                            html = html.replace(/^# (.+)$/gm, '<strong style="display:block;margin:.7rem 0 .3rem;font-size:1rem">$1</strong>');
                                                            html = html.replace(/\*\*(.+?)\*\*/g, '<strong style="color:white;font-weight:600">$1</strong>');
                                                            html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
                                                            html = html.replace(/`([^`]+)`/g, '<code style="background:rgba(0,0,0,0.35);padding:.15rem .35rem;border-radius:4px;font-size:.78rem;color:#a5b4fc">$1</code>');
                                                            html = html.replace(/^- (.+)$/gm, '<li>$1</li>');
                                                            html = html.replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>');
                                                            html = html.replace(/((?:<li>[^]*?<\/li>\s*(?:<br\s*\/?>)?)+)/g, (m) => '<ul style="padding-left:1rem;margin:.3rem 0">' + m.replace(/<br\s*\/?>/g, '') + '</ul>');
                                                            html = html.replace(/\n\n/g, '<br/><br/>');
                                                            html = html.replace(/\n/g, '<br/>');
                                                            return html;
                                                        })()
                                                    }}
                                                />
                                            ) : (
                                                <span>{msg.content}</span>
                                            )}
                                        </div>
                                    ))}

                                    {chatLoading && (
                                        <div className="flex gap-1 self-start" style={{ padding: '0.65rem 0.9rem' }}>
                                            {[0, 1, 2].map(j => (
                                                <div
                                                    key={j}
                                                    className="rounded-full"
                                                    style={{
                                                        width: '6px', height: '6px', background: 'rgba(255,255,255,0.3)',
                                                        animation: 'bounce 1.2s ease-in-out infinite',
                                                        animationDelay: `${j * 0.2}s`,
                                                    }}
                                                />
                                            ))}
                                            <span className="text-xs text-white/30 ml-1.5">Analyzing...</span>
                                        </div>
                                    )}
                                    <div ref={chatEndRef} />
                                </div>

                                {/* Suggestion chips (only when empty) */}
                                {chatMessages.length === 0 && !chatLoading && (
                                    <div className="flex flex-wrap gap-1.5" style={{ padding: '0.5rem 1.25rem 0.25rem' }}>
                                        {[
                                            'What are the top issue categories?',
                                            'Show equity gaps in response times',
                                            'How is resident sentiment trending?',
                                            'What should I prioritize today?',
                                        ].map((q, i) => (
                                            <button
                                                key={i}
                                                onClick={() => { setChatInput(q); }}
                                                className="transition-colors"
                                                style={{
                                                    background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)',
                                                    borderRadius: '100px', padding: '0.35rem 0.75rem', fontSize: '0.72rem',
                                                    color: '#6ee7b7', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {q}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Input area */}
                                <form
                                    onSubmit={sendChatMessage}
                                    className="flex gap-2"
                                    style={{ padding: '0.75rem 1.25rem', borderTop: '1px solid rgba(255,255,255,0.08)', paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
                                >
                                    <textarea
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(); } }}
                                        placeholder="Ask about your data…"
                                        disabled={chatLoading}
                                        rows={1}
                                        maxLength={2000}
                                        style={{
                                            flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                                            borderRadius: '10px', padding: '0.6rem 0.85rem', color: 'white',
                                            fontSize: '16px', fontFamily: 'inherit', outline: 'none', resize: 'none',
                                            maxHeight: '120px', overflowY: 'auto', lineHeight: '1.4',
                                        }}
                                    />
                                    <button
                                        type="submit"
                                        disabled={!chatInput.trim() || chatLoading}
                                        style={{
                                            background: 'linear-gradient(135deg, #10b981, #14b8a6)', border: 'none', borderRadius: '10px',
                                            width: '38px', height: '38px', display: 'grid', placeItems: 'center',
                                            cursor: 'pointer', color: 'white', fontSize: '0.85rem', flexShrink: 0,
                                            opacity: (!chatInput.trim() || chatLoading) ? 0.35 : 1,
                                        }}
                                    >
                                        →
                                    </button>
                                </form>
                            </motion.div>
                        </>
                    )}
                </AnimatePresence>

                {/* Floating AI Chat FAB Button (visible when chat is closed) */}
                {!chatOpen && (currentView === 'statistics' || currentView === 'dashboard') && (
                    <button
                        onClick={() => setChatOpen(true)}
                        className="fixed z-[200] animate-pulse"
                        style={{
                            bottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
                            right: '1.5rem',
                            width: '56px', height: '56px', borderRadius: '50%',
                            background: 'linear-gradient(135deg, #10b981 0%, #14b8a6 100%)',
                            border: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center',
                            boxShadow: '0 4px 24px rgba(16,185,129,0.4)',
                        }}
                        aria-label="Ask AI Analytics Advisor"
                    >
                        <MessageSquare className="w-6 h-6 text-white" style={{ fill: 'none', stroke: 'currentColor', strokeWidth: 2 }} />
                    </button>
                )}

                {/* List/Detail View */}
                {currentView !== 'statistics' && currentView !== 'dashboard' && currentView !== 'reports' && currentView !== 'report' && currentView !== 'deleted_tickets' && (
                    <div className="flex-1 flex min-h-0 h-full">
                        {/* Request List Panel */}
                        <div className="w-full lg:w-96 flex flex-col border-r border-white/10 h-full overflow-hidden relative">
                            {/* List Header — fixed height, never grows */}
                            <div className="p-4 border-b border-white/10 space-y-3 flex-shrink-0">

                                {/* Title row */}
                                <div className="flex items-center justify-between">
                                    <h2 className="text-lg font-semibold text-white">Incidents</h2>
                                    <span className="text-xs text-white/40">{sortedRequests.length} results</span>
                                </div>

                                {/* Assignment tabs */}
                                <div className="flex gap-1.5">
                                    {([
                                        { id: 'me', label: 'Mine', count: quickStats.assignedToMe, color: 'from-primary-500 to-primary-600 shadow-primary-500/40 ring-primary-400/60' },
                                        { id: 'department', label: 'My Dept', count: quickStats.inMyDepartment, color: 'from-purple-500 to-purple-600 shadow-purple-500/40 ring-purple-400/60' },
                                        { id: 'all', label: 'All', count: quickStats.total, color: 'from-slate-500 to-slate-600 shadow-slate-500/40 ring-slate-400/60' },
                                    ] as const).map(tab => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setFilterAssignment(tab.id)}
                                            className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${filterAssignment === tab.id
                                                ? `bg-gradient-to-r ${tab.color} text-white shadow-lg ring-2`
                                                : 'bg-white/5 border border-white/15 text-white/70 hover:bg-white/10 hover:text-white'}`}
                                        >
                                            {tab.label}
                                            <span className={`ml-1 ${filterAssignment === tab.id ? 'text-white/80' : 'text-white/40'}`}>({tab.count})</span>
                                        </button>
                                    ))}
                                </div>

                                {/* Search bar */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                    <input
                                        type="text"
                                        placeholder="Search ID, description, address…"
                                        aria-label="Search requests"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="glass-input pl-9 pr-8 py-2 text-sm w-full"
                                    />
                                    {searchQuery && (
                                        <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white" aria-label="Clear search">
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>

                                {/* Category quick-filter pills — scrollable horizontal row */}
                                {(() => {
                                    // Count per category from the current status-filtered (but not category-filtered) set
                                    const baseForCount = allRequests.filter(r => {
                                        if (currentView === 'active') return r.status === 'open';
                                        if (currentView === 'in_progress') return r.status === 'in_progress';
                                        if (currentView === 'resolved') return r.status === 'closed';
                                        return true; // all_tickets
                                    });
                                    const categoryCounts = services.map(s => ({
                                        code: s.service_code,
                                        name: s.service_name,
                                        count: baseForCount.filter(r => r.service_code === s.service_code).length,
                                    })).filter(c => c.count > 0);
                                    if (categoryCounts.length === 0) return null;
                                    return (
                                        <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-hide -mx-1 px-1">
                                            <button
                                                onClick={() => setFilterService(null)}
                                                className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all border whitespace-nowrap ${filterService === null
                                                    ? 'bg-white/20 border-white/40 text-white'
                                                    : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'}`}
                                            >
                                                All <span className="ml-1 opacity-70">{baseForCount.length}</span>
                                            </button>
                                            {categoryCounts.map(c => (
                                                <button
                                                    key={c.code}
                                                    onClick={() => setFilterService(filterService === c.code ? null : c.code)}
                                                    className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all border whitespace-nowrap ${filterService === c.code
                                                        ? 'bg-primary-500/30 border-primary-500/60 text-primary-200'
                                                        : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'}`}
                                                >
                                                    {c.name} <span className="ml-1 opacity-70">{c.count}</span>
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })()}

                                {/* Sort + Filter toggle row */}
                                <div className="flex gap-2">
                                    {/* Sort dropdown */}
                                    <div className="relative flex-1">
                                        <ArrowUpDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" />
                                        <select
                                            value={sortOrder}
                                            onChange={(e) => setSortOrder(e.target.value as typeof sortOrder)}
                                            className="glass-input text-xs py-2 pl-7 pr-2 w-full appearance-none cursor-pointer"
                                            aria-label="Sort order"
                                        >
                                            <optgroup label="By Date">
                                                <option value="newest">Newest first</option>
                                                <option value="oldest">Oldest first</option>
                                                <option value="updated">Last updated</option>
                                            </optgroup>
                                            <optgroup label="By Category">
                                                <option value="alpha_az">Category A → Z</option>
                                                <option value="alpha_za">Category Z → A</option>
                                            </optgroup>
                                            <optgroup label="By Reporter">
                                                <option value="reporter_name">Reporter name</option>
                                                <option value="reporter_phone">Reporter phone</option>
                                            </optgroup>
                                            <optgroup label="By Priority">
                                                <option value="priority_high">Priority high → low</option>
                                                <option value="priority_low">Priority low → high</option>
                                            </optgroup>
                                            <optgroup label="By Location">
                                                <option value="address">Address A → Z</option>
                                            </optgroup>
                                        </select>
                                    </div>
                                    {/* Filters toggle */}
                                    <button
                                        onClick={() => setShowFilters(!showFilters)}
                                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all border ${showFilters || hasActiveFilters
                                            ? 'bg-primary-500/20 border-primary-500/50 text-primary-300'
                                            : 'bg-white/5 border-white/15 text-white/60 hover:bg-white/10 hover:text-white'}`}
                                        aria-label="Toggle filters"
                                    >
                                        <SlidersHorizontal className="w-3.5 h-3.5" />
                                        Filters
                                        {hasActiveFilters && (
                                            <span className="bg-primary-500 text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                                                {[filterDepartment !== null, filterService !== null, filterPhone.trim(), filterDateFrom, filterDateTo, filterSource !== 'all', filterStaff, mapPriorityFilter !== 'all'].filter(Boolean).length}
                                            </span>
                                        )}
                                    </button>
                                </div>

                                {/* Active filter chips — shown only when drawer is closed */}
                                {hasActiveFilters && !showFilters && (
                                    <div className="flex flex-wrap gap-1">
                                        {filterDepartment !== null && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-300 text-xs">
                                                {departments.find(d => d.id === filterDepartment)?.name ?? 'Dept'}
                                                <button onClick={() => setFilterDepartment(null)} className="hover:text-white"><X className="w-3 h-3" /></button>
                                            </span>
                                        )}
                                        {filterService !== null && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/40 text-purple-300 text-xs">
                                                {services.find(s => s.service_code === filterService)?.service_name ?? 'Category'}
                                                <button onClick={() => setFilterService(null)} className="hover:text-white"><X className="w-3 h-3" /></button>
                                            </span>
                                        )}
                                        {filterPhone.trim() && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/20 border border-green-500/40 text-green-300 text-xs">
                                                📞 {filterPhone}
                                                <button onClick={() => setFilterPhone('')} className="hover:text-white"><X className="w-3 h-3" /></button>
                                            </span>
                                        )}
                                        {(filterDateFrom || filterDateTo) && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-300 text-xs">
                                                📅 {filterDateFrom || '…'}–{filterDateTo || '…'}
                                                <button onClick={() => { setFilterDateFrom(''); setFilterDateTo(''); }} className="hover:text-white"><X className="w-3 h-3" /></button>
                                            </span>
                                        )}
                                        {mapPriorityFilter !== 'all' && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 border border-red-500/40 text-red-300 text-xs">
                                                {mapPriorityFilter === 'high' ? '🔴' : mapPriorityFilter === 'medium' ? '🟡' : '🟢'} {mapPriorityFilter}
                                                <button onClick={() => setMapPriorityFilter('all')} className="hover:text-white"><X className="w-3 h-3" /></button>
                                            </span>
                                        )}
                                        {filterSource !== 'all' && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 text-xs">
                                                {filterSource === 'staff' ? '👷 Staff' : '👤 Resident'}
                                                <button onClick={() => setFilterSource('all')} className="hover:text-white"><X className="w-3 h-3" /></button>
                                            </span>
                                        )}
                                        {filterStaff && (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-500/40 text-indigo-300 text-xs">
                                                👤 {users.find(u => u.username === filterStaff)?.full_name || filterStaff}
                                                <button onClick={() => setFilterStaff('')} className="hover:text-white"><X className="w-3 h-3" /></button>
                                            </span>
                                        )}
                                        <button onClick={clearFilters} className="text-xs text-white/40 hover:text-white/70 underline">Clear all</button>
                                    </div>
                                )}
                            </div>

                            {/* ── Filter drawer: absolutely positioned, slides up from bottom, overlays ticket list ── */}
                            {showFilters && (
                                <div className="absolute inset-0 z-30 flex flex-col justify-end pointer-events-none">
                                    {/* dim backdrop */}
                                    <div className="absolute inset-0 bg-black/50 pointer-events-auto" onClick={() => setShowFilters(false)} />
                                    {/* drawer panel */}
                                    <div className="relative pointer-events-auto bg-[#181d2b] border-t border-white/15 shadow-2xl flex flex-col" style={{ maxHeight: '80%' }}>
                                        {/* Drawer header */}
                                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
                                            <span className="text-sm font-semibold text-white flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Filters</span>
                                            <div className="flex items-center gap-3">
                                                {hasActiveFilters && <button onClick={clearFilters} className="text-xs text-primary-400 hover:text-primary-300">Clear all</button>}
                                                <button onClick={() => setShowFilters(false)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
                                            </div>
                                        </div>
                                        {/* Scrollable content */}
                                        <div className="overflow-y-auto p-4 space-y-4">
                                            {/* Date range */}
                                            <div>
                                                <label className="flex items-center gap-1.5 text-xs font-medium text-white/60 mb-2"><Calendar className="w-3.5 h-3.5" /> Date Range</label>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div><span className="text-[11px] text-white/40 block mb-1">From</span><input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="glass-input text-xs py-1.5 w-full" aria-label="From date" /></div>
                                                    <div><span className="text-[11px] text-white/40 block mb-1">To</span><input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="glass-input text-xs py-1.5 w-full" aria-label="To date" /></div>
                                                </div>
                                            </div>
                                            {/* Phone */}
                                            <div>
                                                <label className="flex items-center gap-1.5 text-xs font-medium text-white/60 mb-2"><Phone className="w-3.5 h-3.5" /> Reporter Phone</label>
                                                <div className="relative">
                                                    <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
                                                    <input type="tel" value={filterPhone} onChange={(e) => setFilterPhone(e.target.value)} placeholder="e.g. 9999999" className="glass-input text-xs py-1.5 pl-8 pr-7 w-full" aria-label="Filter by phone" />
                                                    {filterPhone && <button onClick={() => setFilterPhone('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-white/30 hover:text-white"><X className="w-3 h-3" /></button>}
                                                </div>
                                            </div>
                                            {/* Category + Department */}
                                            <div className="grid grid-cols-2 gap-2">
                                                <div><label className="text-[11px] text-white/40 block mb-1">Category</label><select value={filterService ?? ''} onChange={(e) => setFilterService(e.target.value || null)} className="glass-input text-xs py-1.5 w-full" aria-label="Filter by category"><option value="">All</option>{services.map(s => <option key={s.service_code} value={s.service_code}>{s.service_name}</option>)}</select></div>
                                                <div><label className="text-[11px] text-white/40 block mb-1">Department</label><select value={filterDepartment ?? ''} onChange={(e) => setFilterDepartment(e.target.value ? Number(e.target.value) : null)} className="glass-input text-xs py-1.5 w-full" aria-label="Filter by department"><option value="">All</option>{departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}</select></div>
                                            </div>
                                            {/* Priority pills */}
                                            <div>
                                                <label className="text-[11px] text-white/40 block mb-1.5">Priority</label>
                                                <div className="flex gap-1.5">
                                                    {([{ val: 'all', label: 'All', emoji: '' }, { val: 'high', label: 'High', emoji: '🔴' }, { val: 'medium', label: 'Med', emoji: '🟡' }, { val: 'low', label: 'Low', emoji: '🟢' }] as const).map(p => (
                                                        <button key={p.val} onClick={() => setMapPriorityFilter(p.val)} className={`flex-1 text-xs py-1.5 rounded-lg border transition-all font-medium ${mapPriorityFilter === p.val ? 'bg-white/20 border-white/40 text-white' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'}`}>{p.emoji} {p.label}</button>
                                                    ))}
                                                </div>
                                            </div>
                                            {/* Submitted by pills */}
                                            <div>
                                                <label className="text-[11px] text-white/40 block mb-1.5">Submitted by</label>
                                                <div className="flex gap-1.5">
                                                    {([{ val: 'all', label: 'All', icon: '🌐' }, { val: 'staff', label: 'Staff', icon: '👷' }, { val: 'resident', label: 'Resident', icon: '👤' }] as const).map(s => (
                                                        <button key={s.val} onClick={() => setFilterSource(s.val)} className={`flex-1 text-xs py-1.5 rounded-lg border transition-all font-medium ${filterSource === s.val ? 'bg-cyan-500/25 border-cyan-500/50 text-cyan-200' : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80'}`}>{s.icon} {s.label}</button>
                                                    ))}
                                                </div>
                                            </div>
                                            {/* Assigned staff */}
                                            <div>
                                                <label className="text-[11px] text-white/40 block mb-1.5">Assigned to staff</label>
                                                <select value={filterStaff} onChange={(e) => setFilterStaff(e.target.value)} className="glass-input text-xs py-1.5 w-full" aria-label="Filter by assigned staff member">
                                                    <option value="">Any staff member</option>
                                                    {users.map(u => <option key={u.username} value={u.username}>{u.full_name ? `${u.full_name} (@${u.username})` : u.username}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Request List */}
                            <div className="flex-1 overflow-auto overscroll-contain">
                                {isLoading ? (
                                    <div className="flex justify-center py-12">
                                        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                ) : sortedRequests.length === 0 ? (
                                    <div className="text-center py-12 text-white/50">
                                        No incidents found
                                    </div>
                                ) : (() => {
                                    // Separate requests into needs priority and others
                                    // Only render the visible window for performance
                                    const allNeedsPriority = sortedRequests.filter(r => {
                                        const ai = r.ai_analysis as any;
                                        return ai?.priority_score != null && r.manual_priority_score == null;
                                    }).sort((a, b) => new Date(b.requested_datetime).getTime() - new Date(a.requested_datetime).getTime());

                                    const allOther = sortedRequests.filter(r => {
                                        const ai = r.ai_analysis as any;
                                        return !(ai?.priority_score != null && r.manual_priority_score == null);
                                    });

                                    // Render a single request item
                                    const renderRequest = (request: typeof sortedRequests[0]) => (
                                        <motion.button
                                            key={request.id}
                                            onClick={() => loadRequestDetail(request.service_request_id)}
                                            className={`w-full text-left p-4 hover:bg-white/5 transition-colors ${selectedRequest?.service_request_id === request.service_request_id
                                                ? 'bg-white/10'
                                                : ''
                                                }`}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-mono text-xs text-white/60">
                                                        {request.service_request_id}
                                                    </span>
                                                    {/* NEW badge for requests < 24 hours old */}
                                                    {Date.now() - new Date(request.requested_datetime).getTime() < 24 * 60 * 60 * 1000 && (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-semibold animate-pulse">
                                                            NEW
                                                        </span>
                                                    )}
                                                </div>
                                                <StatusBadge status={request.status} />
                                            </div>
                                            <h3 className="font-medium text-white mb-1">{request.service_name}</h3>
                                            <p className="text-sm text-white/70 line-clamp-2">{request.description}</p>
                                            <div className="flex items-center justify-between mt-2">
                                                <div className="flex items-center gap-2 text-xs text-white/60">
                                                    <Clock className="w-3 h-3" />
                                                    {new Date(request.requested_datetime).toLocaleDateString()}
                                                </div>
                                                {/* Priority indicator */}
                                                {request.assigned_to === user?.username ? (
                                                    <span className="text-xs px-2 py-0.5 rounded-full bg-primary-500/20 text-primary-400">
                                                        🎯 Mine
                                                    </span>
                                                ) : request.assigned_department_id && userDepartmentIds.includes(request.assigned_department_id) && !request.assigned_to ? (
                                                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-200">
                                                        🏢 Dept
                                                    </span>
                                                ) : null}
                                            </div>
                                        </motion.button>
                                    );

                                    // Apply virtual window: only render first `visibleCount` items total
                                    let remaining = visibleCount;
                                    const needsPriorityRequests = allNeedsPriority.slice(0, remaining);
                                    remaining = Math.max(0, remaining - needsPriorityRequests.length);
                                    const otherRequests = allOther.slice(0, remaining);
                                    const hasMore = sortedRequests.length > visibleCount;

                                    return (
                                        <div>
                                            {/* Needs Priority Review Section - Bordered box at top */}
                                            {needsPriorityRequests.length > 0 && (
                                                <div className="m-3 rounded-xl border-2 border-amber-500/40 bg-amber-500/5 overflow-hidden">
                                                    {/* Chip Header */}
                                                    <div className="flex items-center justify-center py-2 bg-amber-500/10 border-b border-amber-500/20">
                                                        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/50 text-amber-400 text-sm font-semibold">
                                                            <span className="relative flex h-2 w-2">
                                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                                                            </span>
                                                            Needs Priority Review ({allNeedsPriority.length})
                                                        </span>
                                                    </div>
                                                    {/* Requests in this section */}
                                                    <div className="divide-y divide-amber-500/10">
                                                        {needsPriorityRequests.map(renderRequest)}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Other Requests */}
                                            {otherRequests.length > 0 && (
                                                <div className="divide-y divide-white/5">
                                                    {otherRequests.map(renderRequest)}
                                                </div>
                                            )}

                                            {/* Sentinel + load-more indicator */}
                                            <div ref={listSentinelRef} className="h-4" />
                                            {hasMore && (
                                                <div className="text-center py-3 text-xs text-white/30">
                                                    Showing {visibleCount} of {sortedRequests.length} — scroll for more
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Detail Panel - Shows as overlay on mobile, side panel on desktop */}
                        <div
                            ref={detailPanelRef}
                            className={`${selectedRequest ? 'fixed inset-0 z-50 lg:relative lg:inset-auto overflow-y-auto overscroll-contain touch-pan-y' : 'hidden'} lg:flex flex-1 flex-col bg-slate-900 lg:overflow-y-auto h-full`}
                        >
                            {/* Mobile Back Button */}
                            {selectedRequest && (
                                <div className="lg:hidden p-3 border-b border-white/10 flex items-center">
                                    <button
                                        onClick={() => setSelectedRequest(null)}
                                        className="flex items-center gap-2 text-white/70 hover:text-white"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                        <span>Back to List</span>
                                    </button>
                                </div>
                            )}
                            {selectedRequest ? (
                                <div className="flex-1 flex flex-col">
                                    {/* Sticky Header with Actions & Assignment - Premium Glass Style */}
                                    <div className="sticky top-0 z-10 bg-gradient-to-b from-slate-900/95 via-slate-800/95 to-slate-800/90 backdrop-blur-md border-b border-white/10 p-3 sm:p-4 space-y-2 sm:space-y-3">
                                        {/* Row 1: Title, ID, Status and Print */}
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1 flex-wrap">
                                                    <span className="font-mono text-[10px] sm:text-xs text-white/50 bg-white/5 px-1.5 sm:px-2 py-0.5 rounded">{selectedRequest.service_request_id}</span>
                                                    <StatusBadge status={selectedRequest.status} />
                                                </div>
                                                <h1 className="text-base sm:text-lg font-semibold text-white truncate">{selectedRequest.service_name}</h1>
                                            </div>
                                            <PrintWorkOrder
                                                request={selectedRequest}
                                                auditLog={auditLog}
                                                comments={comments}
                                                townshipName={settings?.township_name}
                                                logoUrl={settings?.logo_url || undefined}
                                                mapsApiKey={mapsConfig?.google_maps_api_key}
                                            />
                                        </div>

                                        {/* Row 2: Status Actions - Compact on mobile */}
                                        <div className="flex gap-1.5 sm:gap-2">
                                            <button onClick={() => handleStatusChange('open')} className={`flex-1 py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all ${selectedRequest.status === 'open' ? 'bg-gradient-to-r from-purple-500 to-violet-600 text-white shadow-lg shadow-purple-500/30 ring-2 ring-white/20' : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:text-white'}`}>Open</button>
                                            <button onClick={() => handleStatusChange('in_progress')} className={`flex-1 py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all ${selectedRequest.status === 'in_progress' ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30 ring-2 ring-white/20' : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:text-white'}`}>In Progress</button>
                                            <button onClick={() => handleStatusChange('closed')} className={`flex-1 py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all ${selectedRequest.status === 'closed' ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/30 ring-2 ring-white/20' : 'bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:text-white'}`}>Closed</button>
                                        </div>

                                        {/* Row 3: Assignment Dropdowns - More compact on mobile */}
                                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                                            <select
                                                value={editAssignment?.departmentId ?? selectedRequest.assigned_department_id ?? ''}
                                                onChange={(e) => { const val = e.target.value ? Number(e.target.value) : null; setEditAssignment(prev => ({ departmentId: val, assignedTo: prev?.assignedTo ?? selectedRequest.assigned_to ?? null })); }}
                                                className="flex-1 min-w-0 py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg bg-white/5 border border-white/10 text-white text-xs sm:text-sm focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/25 transition-all [&>option]:bg-slate-800 [&>option]:text-white"
                                                aria-label="Assign to department"
                                            >
                                                <option value="" className="text-white/50">Department...</option>
                                                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                                            </select>
                                            <select
                                                value={editAssignment?.assignedTo ?? selectedRequest.assigned_to ?? ''}
                                                onChange={(e) => { const val = e.target.value; setEditAssignment(prev => ({ departmentId: prev?.departmentId ?? selectedRequest.assigned_department_id ?? null, assignedTo: val })); }}
                                                className="flex-1 min-w-0 py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg bg-white/5 border border-white/10 text-white text-xs sm:text-sm focus:border-primary-500/50 focus:ring-1 focus:ring-primary-500/25 transition-all [&>option]:bg-slate-800 [&>option]:text-white"
                                                aria-label="Assign to staff member"
                                            >
                                                <option value="">Assignee...</option>
                                                {(() => {
                                                    const deptId = editAssignment?.departmentId ?? selectedRequest.assigned_department_id;
                                                    const filteredUsers = deptId ? users.filter(u => u.departments?.some(d => d.id === deptId)) : users;
                                                    return filteredUsers.map(u => (
                                                        <option key={u.id} value={u.username}>
                                                            {u.full_name || u.username}
                                                        </option>
                                                    ));
                                                })()}
                                            </select>
                                            {editAssignment && (
                                                <button onClick={async () => {
                                                    if (demoGuard()) return;
                                                    setIsSavingAssignment(true);
                                                    try {
                                                        const updated = await api.updateRequest(selectedRequest.service_request_id, {
                                                            assigned_department_id: editAssignment.departmentId ?? undefined,
                                                            assigned_to: editAssignment.assignedTo === null ? '' : editAssignment.assignedTo
                                                        });
                                                        setSelectedRequest(updated);
                                                        // Optimistic update: update the request in both lists
                                                        setAllRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
                                                        setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
                                                        setEditAssignment(null);
                                                        loadAuditLog(selectedRequest.service_request_id);
                                                    } catch (err) {
                                                        console.error(err);
                                                    } finally {
                                                        setIsSavingAssignment(false);
                                                    }
                                                }} disabled={isSavingAssignment} className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-xs sm:text-sm font-medium disabled:opacity-50 transition-all shadow-lg shadow-primary-500/20">{isSavingAssignment ? '...' : 'Save'}</button>
                                            )}
                                        </div>

                                        {/* Row 4: Legal Hold Toggle (Admin Only) - Hidden on mobile, shown in menu */}
                                        {user?.role === 'admin' && (
                                            <button
                                                onClick={async () => {
                                                    if (demoGuard()) return;
                                                    if (selectedRequest.flagged !== true) {
                                                        // Show confirmation before placing on legal hold
                                                        if (window.confirm('Place this request under Legal Hold?\n\nThis will prevent the record from being archived or deleted by the retention policy.\n\nContinue?')) {
                                                            try {
                                                                const updated = await api.updateRequest(selectedRequest.service_request_id, {
                                                                    flagged: true
                                                                });
                                                                setSelectedRequest(updated);
                                                                setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
                                                            } catch (err) {
                                                                console.error('Failed to enable legal hold:', err);
                                                            }
                                                        }
                                                    } else {
                                                        // Allow direct removal without confirmation
                                                        try {
                                                            const updated = await api.updateRequest(selectedRequest.service_request_id, {
                                                                flagged: false
                                                            });
                                                            setSelectedRequest(updated);
                                                            setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
                                                        } catch (err) {
                                                            console.error('Failed to remove legal hold:', err);
                                                        }
                                                    }
                                                }}
                                                className={`w-full py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 ${selectedRequest.flagged === true
                                                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/30 ring-2 ring-white/20'
                                                    : 'bg-white/5 border border-white/10 text-white/70 hover:bg-amber-500/20 hover:text-amber-400 hover:border-amber-500/30'
                                                    }`}
                                                aria-label={selectedRequest.flagged === true ? 'Remove legal hold' : 'Place on legal hold'}
                                            >
                                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                                                </svg>
                                                <span className="hidden sm:inline">{selectedRequest.flagged === true ? 'Under Legal Hold (Click to Remove)' : 'Place on Legal Hold'}</span>
                                                <span className="sm:hidden">{selectedRequest.flagged === true ? 'Legal Hold ✓' : 'Legal Hold'}</span>
                                            </button>
                                        )}

                                        {/* Row 5: Public Visibility Toggle (Admin Only) */}
                                        {user?.role === 'admin' && (
                                            <button
                                                onClick={async () => {
                                                    if (demoGuard()) return;
                                                    const newValue = !selectedRequest.hidden_from_public;
                                                    try {
                                                        const updated = await api.updateRequest(selectedRequest.service_request_id, {
                                                            hidden_from_public: newValue
                                                        });
                                                        setSelectedRequest(updated);
                                                        setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
                                                    } catch (err) {
                                                        console.error('Failed to update visibility:', err);
                                                    }
                                                }}
                                                className={`w-full py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1.5 sm:gap-2 ${selectedRequest.hidden_from_public === true
                                                    ? 'bg-white/5 border border-white/10 text-white/70 hover:bg-emerald-500/20 hover:text-emerald-400 hover:border-emerald-500/30'
                                                    : 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/30 ring-2 ring-white/20'
                                                    }`}
                                                aria-label={selectedRequest.hidden_from_public === true ? 'Make visible to public' : 'Hide from public'}
                                            >
                                                {selectedRequest.hidden_from_public === true ? (
                                                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                    </svg>
                                                ) : (
                                                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                )}
                                                <span className="hidden sm:inline">{selectedRequest.hidden_from_public === true ? 'Hidden from Public (Click to Make Visible)' : 'Visible to Public (Click to Hide)'}</span>
                                                <span className="sm:hidden">{selectedRequest.hidden_from_public === true ? '🔒 Hidden' : '🌐 Public'}</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* Scrollable Content - Professional Government Styling */}
                                    <div className="flex-1 overflow-auto p-4 space-y-4">

                                        {/* ═══ SECTION 1: Request Details (Description + AI + Photos) ═══ */}
                                        <div className="p-4 rounded-lg bg-slate-800/50 border border-white/10">
                                            <p className="text-white/90 leading-relaxed mb-4">{selectedRequest.description}</p>

                                            {/* Custom Fields / Additional Information - Right below description */}
                                            {selectedRequest.custom_fields && Object.keys(selectedRequest.custom_fields).length > 0 && (
                                                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-4">
                                                    <p className="text-amber-400 font-medium text-sm mb-2 flex items-center gap-2">
                                                        <FileText className="w-4 h-4" />
                                                        Additional Information
                                                    </p>
                                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                                        {Object.entries(selectedRequest.custom_fields).map(([key, value]) => (
                                                            <div key={key} className="flex flex-col">
                                                                <span className="text-white/40 text-xs capitalize">{key.replace(/_/g, ' ')}</span>
                                                                <span className="text-white/80">{Array.isArray(value) ? value.join(', ') : String(value)}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Photos - Now ABOVE AI Analysis */}
                                            {selectedRequest.media_urls && selectedRequest.media_urls.length > 0 && (
                                                <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
                                                    {selectedRequest.media_urls.map((url, i) => (
                                                        <img key={i} src={url} alt={`Photo ${i + 1}`} className="w-28 h-20 flex-shrink-0 object-cover rounded-lg cursor-pointer hover:opacity-80 ring-1 ring-white/10" onClick={() => setLightboxUrl(url)} />
                                                    ))}
                                                </div>
                                            )}

                                            {/* AI Analysis - Premium Enhanced Display (Now BELOW Photos) */}
                                            {(() => {
                                                const ai = selectedRequest.ai_analysis as any;
                                                const priorityScore = ai?.priority_score ?? null;
                                                const qualitativeText = ai?.qualitative_analysis ?? selectedRequest.vertex_ai_summary ?? null;
                                                const hasError = ai?._error;

                                                if (!ai && !qualitativeText) return null;

                                                return (
                                                    <div
                                                        className={`relative overflow-hidden rounded-xl bg-slate-800/40 border border-white/10 mb-4 backdrop-blur-sm ${!isAIExpanded ? 'cursor-pointer hover:bg-slate-800/60 transition-colors' : ''}`}
                                                        onClick={!isAIExpanded ? () => setIsAIExpanded(true) : undefined}
                                                    >
                                                        {/* Professional accent line */}
                                                        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500/50 via-purple-500/50 to-primary-500/50" />

                                                        <div className="p-4">
                                                            {/* Simple Header - Just label and expand toggle */}
                                                            <button
                                                                onClick={() => setIsAIExpanded(!isAIExpanded)}
                                                                className="w-full flex items-center justify-between mb-3"
                                                            >
                                                                <div className="flex items-center gap-3">
                                                                    <div className="p-2 rounded-xl bg-gradient-to-br from-primary-500/20 to-purple-500/20 ring-1 ring-white/10">
                                                                        <Brain className="w-5 h-5 text-primary-400" />
                                                                    </div>
                                                                    <div className="text-left">
                                                                        <span className="text-sm font-semibold text-white">AI Analysis</span>
                                                                        <p className="text-[10px] text-white/40">Gemini 3.1 Flash-Lite</p>
                                                                    </div>
                                                                </div>
                                                                <ChevronDown className={`w-5 h-5 text-white/40 transition-transform ${isAIExpanded ? 'rotate-180' : ''}`} />
                                                            </button>

                                                            {/* Translation block for non-English submissions */}
                                                            {ai?.translation && ai.translation.detected_language && ai.translation.detected_language !== 'English' && ai.translation.english_translation && (
                                                                <div className="mb-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                                                                    <p className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">
                                                                        🌐 Translation ({ai.translation.detected_language})
                                                                    </p>
                                                                    <p className="text-xs text-white/40 italic mb-1">"{ai.translation.original_text}"</p>
                                                                    <p className="text-sm text-white/80">"{ai.translation.english_translation}"</p>
                                                                </div>
                                                            )}

                                                            {/* Summary Text - Always visible */}
                                                            {qualitativeText && !hasError && (
                                                                <p className={`text-sm text-white/70 leading-relaxed mb-4 ${!isAIExpanded ? 'line-clamp-2' : ''}`}>
                                                                    {qualitativeText}
                                                                </p>
                                                            )}

                                                            {/* Priority Actions - Large, Easy to Tap Buttons */}
                                                            {(priorityScore || selectedRequest.manual_priority_score) && !hasError && (
                                                                <div className="space-y-4">
                                                                    {/* Priority Display */}
                                                                    <div className={`flex items-center justify-between p-3 rounded-xl ${(selectedRequest.manual_priority_score ?? priorityScore) >= 8 ? 'bg-red-500/10 border border-red-500/20' :
                                                                        (selectedRequest.manual_priority_score ?? priorityScore) >= 6 ? 'bg-amber-500/10 border border-amber-500/20' :
                                                                            (selectedRequest.manual_priority_score ?? priorityScore) >= 4 ? 'bg-blue-500/10 border border-blue-500/20' :
                                                                                'bg-green-500/10 border border-green-500/20'
                                                                        }`}>
                                                                        <div>
                                                                            <p className="text-xs text-white/50 mb-0.5">
                                                                                {selectedRequest.manual_priority_score ? 'Confirmed Priority' : 'AI Suggested Priority'}
                                                                            </p>
                                                                            <p className={`text-2xl font-bold ${(selectedRequest.manual_priority_score ?? priorityScore) >= 8 ? 'text-red-400' :
                                                                                (selectedRequest.manual_priority_score ?? priorityScore) >= 6 ? 'text-amber-400' :
                                                                                    (selectedRequest.manual_priority_score ?? priorityScore) >= 4 ? 'text-blue-400' :
                                                                                        'text-green-400'
                                                                                }`}>
                                                                                {Number(selectedRequest.manual_priority_score ?? priorityScore).toFixed(1)}
                                                                                <span className="text-sm font-normal opacity-50"> / 10</span>
                                                                            </p>
                                                                        </div>
                                                                        {selectedRequest.manual_priority_score && (
                                                                            <div className="flex items-center gap-1 text-emerald-400">
                                                                                <Check className="w-5 h-5" />
                                                                                <span className="text-xs font-medium">Confirmed</span>
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {/* Action Buttons - Full Width, Easy to Tap */}
                                                                    {priorityScore && !selectedRequest.manual_priority_score && (
                                                                        <div className="flex gap-2 mt-3 mb-4">
                                                                            <button
                                                                                onClick={async () => {
                                                                                    setIsUpdatingPriority(true);
                                                                                    try {
                                                                                        await api.acceptAiPriority(selectedRequest.service_request_id);
                                                                                        setSelectedRequest(prev => prev ? { ...prev, manual_priority_score: priorityScore } : null);
                                                                                        setAllRequests(prev => prev.map(r =>
                                                                                            r.service_request_id === selectedRequest.service_request_id
                                                                                                ? { ...r, manual_priority_score: priorityScore }
                                                                                                : r
                                                                                        ));
                                                                                    } catch (e) {
                                                                                        console.error('Failed to accept AI priority:', e);
                                                                                    } finally {
                                                                                        setIsUpdatingPriority(false);
                                                                                    }
                                                                                }}
                                                                                disabled={isUpdatingPriority}
                                                                                className="flex-1 py-3 px-4 rounded-xl bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 font-semibold text-sm transition-all flex items-center justify-center gap-2 border border-emerald-500/30 active:scale-[0.98]"
                                                                            >
                                                                                <Check className="w-5 h-5" />
                                                                                Accept AI Priority
                                                                            </button>
                                                                            <button
                                                                                onClick={() => {
                                                                                    setShowPriorityEditor(!showPriorityEditor);
                                                                                    setPendingPriority(selectedRequest.manual_priority_score ?? priorityScore ?? 5);
                                                                                }}
                                                                                className="py-3 px-4 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 font-medium text-sm transition-all flex items-center justify-center gap-2 border border-white/10 active:scale-[0.98]"
                                                                            >
                                                                                <Edit3 className="w-4 h-4" />
                                                                                Edit
                                                                            </button>
                                                                        </div>
                                                                    )}

                                                                    {/* Edit button when already confirmed */}
                                                                    {selectedRequest.manual_priority_score && (
                                                                        <button
                                                                            onClick={() => {
                                                                                setShowPriorityEditor(!showPriorityEditor);
                                                                                setPendingPriority(selectedRequest.manual_priority_score ?? priorityScore ?? 5);
                                                                            }}
                                                                            className="w-full py-2.5 px-4 rounded-xl bg-white/5 hover:bg-white/10 text-white/60 font-medium text-sm transition-all flex items-center justify-center gap-2 border border-white/10"
                                                                        >
                                                                            <Edit3 className="w-4 h-4" />
                                                                            Change Priority
                                                                        </button>
                                                                    )}

                                                                    {/* Priority Editor - Full Width Inline */}
                                                                    {showPriorityEditor && (
                                                                        <div className="p-4 rounded-xl bg-slate-800/80 border border-white/10 space-y-4 max-w-md">
                                                                            <p className="text-sm text-white/70 font-medium">Set Priority Level</p>
                                                                            <div className="grid grid-cols-5 gap-2">
                                                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(p => (
                                                                                    <button
                                                                                        key={p}
                                                                                        onClick={() => setPendingPriority(p)}
                                                                                        className={`aspect-square md:aspect-auto md:h-10 rounded-xl text-sm md:text-base font-bold transition-all active:scale-95 ${pendingPriority === p
                                                                                            ? 'bg-primary-500 text-white ring-2 ring-primary-400 ring-offset-2 ring-offset-slate-800'
                                                                                            : p >= 8 ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                                                                                                : p >= 6 ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                                                                                                    : p >= 4 ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                                                                                                        : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                                                                            }`}
                                                                                    >
                                                                                        {p}
                                                                                    </button>
                                                                                ))}
                                                                            </div>
                                                                            <div className="flex gap-2">
                                                                                <button
                                                                                    onClick={async () => {
                                                                                        if (demoGuard()) return;
                                                                                        if (!pendingPriority) return;
                                                                                        setIsUpdatingPriority(true);
                                                                                        try {
                                                                                            await api.updateRequest(
                                                                                                selectedRequest.service_request_id,
                                                                                                { manual_priority_score: pendingPriority }
                                                                                            );
                                                                                            setSelectedRequest(prev => prev ? { ...prev, manual_priority_score: pendingPriority } : null);
                                                                                            setAllRequests(prev => prev.map(r =>
                                                                                                r.service_request_id === selectedRequest.service_request_id
                                                                                                    ? { ...r, manual_priority_score: pendingPriority }
                                                                                                    : r
                                                                                            ));
                                                                                            setShowPriorityEditor(false);
                                                                                        } catch (e) {
                                                                                            console.error('Failed to update priority:', e);
                                                                                        } finally {
                                                                                            setIsUpdatingPriority(false);
                                                                                        }
                                                                                    }}
                                                                                    disabled={isUpdatingPriority}
                                                                                    className="flex-1 py-3 rounded-xl bg-primary-500 hover:bg-primary-600 text-white font-semibold text-sm disabled:opacity-50 transition-all"
                                                                                >
                                                                                    {isUpdatingPriority ? 'Saving...' : 'Save Priority'}
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => setShowPriorityEditor(false)}
                                                                                    className="py-3 px-5 rounded-xl bg-white/10 hover:bg-white/20 text-white/70 font-medium text-sm transition-all"
                                                                                >
                                                                                    Cancel
                                                                                </button>
                                                                            </div>
                                                                            {selectedRequest.manual_priority_score && (
                                                                                <button
                                                                                    onClick={async () => {
                                                                                        if (demoGuard()) return;
                                                                                        setIsUpdatingPriority(true);
                                                                                        try {
                                                                                            await api.updateRequest(
                                                                                                selectedRequest.service_request_id,
                                                                                                { manual_priority_score: null as any }
                                                                                            );
                                                                                            setSelectedRequest(prev => prev ? { ...prev, manual_priority_score: null } : null);
                                                                                            setAllRequests(prev => prev.map(r =>
                                                                                                r.service_request_id === selectedRequest.service_request_id
                                                                                                    ? { ...r, manual_priority_score: null }
                                                                                                    : r
                                                                                            ));
                                                                                            setShowPriorityEditor(false);
                                                                                        } catch (e) {
                                                                                            console.error('Failed to clear priority:', e);
                                                                                        } finally {
                                                                                            setIsUpdatingPriority(false);
                                                                                        }
                                                                                    }}
                                                                                    disabled={isUpdatingPriority}
                                                                                    className="w-full py-2 text-xs text-white/40 hover:text-white/60 transition-colors"
                                                                                >
                                                                                    Reset to AI Suggestion
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {/* Collapsible AI Details */}
                                                            {isAIExpanded && (
                                                                <>
                                                                    {/* Content Moderation Warning */}
                                                                    {ai?.content_flags && ai.content_flags.length > 0 && !ai.content_flags.includes('none') && (
                                                                        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-start gap-2.5">
                                                                            <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                                                                            <div>
                                                                                <p className="text-xs font-bold text-red-400 uppercase tracking-tight">Content Warning</p>
                                                                                <p className="text-xs text-red-300/80">AI detected: {ai.content_flags.join(', ').replace(/_/g, ' ')}</p>
                                                                            </div>
                                                                        </div>
                                                                    )}



                                                                    {/* Priority Justification Quote */}
                                                                    {ai?.priority_justification && !hasError && (
                                                                        <div className="relative pl-4 mt-6 mb-5 border-l-2 border-primary-500/20">
                                                                            <p className="text-white/40 text-xs italic leading-relaxed">"{ai.priority_justification}"</p>
                                                                        </div>
                                                                    )}

                                                                    {/* Photo Assessment - New Section */}
                                                                    {ai?.photo_assessment && !hasError && (
                                                                        <div className="mb-5 p-3 rounded-lg bg-white/5 border border-white/5 space-y-2.5">
                                                                            <div className="flex items-center gap-1.5 opacity-50">
                                                                                <Camera className="w-3.5 h-3.5" />
                                                                                <span className="text-[10px] font-bold uppercase tracking-wider">Visual Triage Assessment</span>
                                                                            </div>
                                                                            <div className="grid grid-cols-1 gap-2">
                                                                                <div className="flex justify-between items-center text-xs">
                                                                                    <span className="text-white/40">Physical Scale</span>
                                                                                    <span className="text-white/80 font-medium">{ai.photo_assessment.physical_scale}</span>
                                                                                </div>
                                                                                <div className="flex justify-between items-center text-xs">
                                                                                    <span className="text-white/40">Blocking Severity</span>
                                                                                    <span className={`font-bold ${ai.photo_assessment.blocking_severity === 'full_block' ? 'text-red-400' :
                                                                                        ai.photo_assessment.blocking_severity === 'partial' ? 'text-amber-400' : 'text-green-400'
                                                                                        }`}>{ai.photo_assessment.blocking_severity?.replace('_', ' ').toUpperCase()}</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Quantitative Metrics - Cleaned up Grid */}
                                                                    {ai?.quantitative_metrics && !hasError && (
                                                                        <div className="grid grid-cols-2 gap-2 mb-4">
                                                                            {ai.quantitative_metrics.estimated_severity && ai.quantitative_metrics.estimated_severity !== 'unknown' && (
                                                                                <div className="p-2.5 rounded-lg bg-white/5 border border-white/5">
                                                                                    <p className="text-[9px] uppercase tracking-wider text-white/30 mb-0.5">Severity</p>
                                                                                    <p className={`text-xs font-bold ${ai.quantitative_metrics.estimated_severity === 'critical' ? 'text-red-400' :
                                                                                        ai.quantitative_metrics.estimated_severity === 'high' ? 'text-amber-400' :
                                                                                            ai.quantitative_metrics.estimated_severity === 'medium' ? 'text-blue-400' : 'text-green-400'
                                                                                        }`}>{ai.quantitative_metrics.estimated_severity.toUpperCase()}</p>
                                                                                </div>
                                                                            )}
                                                                            {ai.quantitative_metrics.systemic_failure_probability !== undefined && (
                                                                                <div className="p-2.5 rounded-lg bg-white/5 border border-white/5">
                                                                                    <p className="text-[9px] uppercase tracking-wider text-white/30 mb-0.5">Systemic Risk</p>
                                                                                    <div className="flex items-center gap-1.5">
                                                                                        <p className={`text-xs font-bold ${ai.quantitative_metrics.systemic_failure_probability > 0.7 ? 'text-red-400' : 'text-primary-400'}`}>
                                                                                            {(ai.quantitative_metrics.systemic_failure_probability * 100).toFixed(0)}%
                                                                                        </p>
                                                                                        <Activity className={`w-3 h-3 ${ai.quantitative_metrics.systemic_failure_probability > 0.7 ? 'text-red-400' : 'text-primary-400'}`} />
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {/* Diagnostic Context - New Section */}
                                                                    {ai?.diagnostic_context && !hasError && (
                                                                        <div className="mb-4 space-y-2">
                                                                            {ai.diagnostic_context.infrastructure_proximity && (
                                                                                <div className="px-3 py-2 rounded-lg bg-blue-500/5 border border-blue-500/10 flex items-start gap-2.5">
                                                                                    <Shield className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                                                                                    <div className="flex-1">
                                                                                        <p className="text-[9px] font-bold text-blue-400 uppercase tracking-tight">Infrastructure Proximity</p>
                                                                                        <p className="text-[11px] text-blue-200/70">
                                                                                            {typeof ai.diagnostic_context.infrastructure_proximity === 'object'
                                                                                                ? ai.diagnostic_context.infrastructure_proximity.details
                                                                                                : ai.diagnostic_context.infrastructure_proximity}
                                                                                        </p>
                                                                                        {ai.diagnostic_context.infrastructure_proximity?.evidence && (
                                                                                            <p className="mt-1 text-[9px] text-blue-400/50 font-medium italic">
                                                                                                Evidence: {ai.diagnostic_context.infrastructure_proximity.evidence}
                                                                                            </p>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            {ai.diagnostic_context.historical_trend && (
                                                                                <div className="px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/10 flex items-start gap-2.5">
                                                                                    <History className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                                                                                    <div className="flex-1">
                                                                                        <p className="text-[9px] font-bold text-amber-400 uppercase tracking-tight">Historical Trend</p>
                                                                                        <p className="text-[11px] text-amber-200/70">
                                                                                            {typeof ai.diagnostic_context.historical_trend === 'object'
                                                                                                ? ai.diagnostic_context.historical_trend.details
                                                                                                : ai.diagnostic_context.historical_trend}
                                                                                        </p>
                                                                                        {ai.diagnostic_context.historical_trend?.evidence && (
                                                                                            <p className="mt-1 text-[9px] text-amber-400/50 font-medium italic">
                                                                                                Evidence: {ai.diagnostic_context.historical_trend.evidence}
                                                                                            </p>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                            {ai.diagnostic_context.weather_impact && ai.diagnostic_context.weather_impact !== 'None' && (
                                                                                <div className="px-3 py-2 rounded-lg bg-primary-500/5 border border-primary-500/10 flex items-start gap-2.5">
                                                                                    <Cloud className="w-3.5 h-3.5 text-primary-400 mt-0.5 flex-shrink-0" />
                                                                                    <div className="flex-1">
                                                                                        <p className="text-[9px] font-bold text-primary-400 uppercase tracking-tight">Weather Criticality</p>
                                                                                        <p className="text-[11px] text-primary-200/70">
                                                                                            {typeof ai.diagnostic_context.weather_impact === 'object'
                                                                                                ? ai.diagnostic_context.weather_impact.details
                                                                                                : ai.diagnostic_context.weather_impact}
                                                                                        </p>
                                                                                        {ai.diagnostic_context.weather_impact?.evidence && (
                                                                                            <p className="mt-1 text-[9px] text-primary-400/50 font-medium italic">
                                                                                                Evidence: {ai.diagnostic_context.weather_impact.evidence}
                                                                                            </p>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}

                                                                    {/* Similar Reports - Clickable Links */}
                                                                    {ai.similar_reports && ai.similar_reports.length > 0 && (
                                                                        <div className="mb-4 px-3 py-2 rounded-lg bg-purple-500/5 border border-purple-500/10 flex items-start gap-2.5">
                                                                            <Link2 className="w-3.5 h-3.5 text-purple-400 mt-0.5 flex-shrink-0" />
                                                                            <div className="flex-1">
                                                                                <p className="text-[9px] font-bold text-purple-400 uppercase tracking-tight">Similar Reports</p>
                                                                                <div className="mt-1 space-y-1">
                                                                                    {ai.similar_reports.map((report: { id: string; description: string; similarity: number; justification?: string }) => (
                                                                                        <button
                                                                                            key={report.id}
                                                                                            onClick={() => {
                                                                                                window.location.hash = `detail/${report.id}`;
                                                                                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                                            }}
                                                                                            title={report.justification || `${Math.round(report.similarity * 100)}% match`}
                                                                                            className="w-full text-left px-2 py-1.5 rounded bg-purple-500/10 hover:bg-purple-500/20 transition-colors group"
                                                                                        >
                                                                                            <div className="flex items-center justify-between gap-2">
                                                                                                <span className="text-[10px] text-purple-300 font-mono group-hover:text-purple-200">{report.id}</span>
                                                                                                <span className="text-[9px] text-purple-400/60">{Math.round(report.similarity * 100)}% match</span>
                                                                                            </div>
                                                                                            <p className="text-[9px] text-purple-200/50 mt-0.5 line-clamp-1">{report.description}</p>
                                                                                            {report.justification && (
                                                                                                <p className="text-[8px] text-purple-400/40 mt-0.5 italic">{report.justification}</p>
                                                                                            )}
                                                                                        </button>
                                                                                    ))}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Safety Flags - Cleaned up Pills */}
                                                                    {ai?.safety_flags && Array.isArray(ai.safety_flags) && ai.safety_flags.length > 0 && (
                                                                        <div className="flex flex-wrap gap-1.5 mb-4">
                                                                            {ai.safety_flags.map((flag: string, i: number) => (
                                                                                <span key={i} className="inline-flex items-center px-2 py-1 rounded bg-red-500/10 text-red-400 text-[10px] font-bold ring-1 ring-red-500/20">
                                                                                    {flag.replace(/_/g, ' ').toUpperCase()}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    )}

                                                                    {/* Footer with timestamp */}
                                                                    <div className="flex items-center justify-between pt-3 border-t border-white/5">
                                                                        {selectedRequest.vertex_ai_analyzed_at && (
                                                                            <p className="text-white/20 text-[9px]">
                                                                                Analyzed {new Date(selectedRequest.vertex_ai_analyzed_at).toLocaleString()}
                                                                            </p>
                                                                        )}
                                                                        {!hasError && (
                                                                            <div className="flex items-center gap-1.5">
                                                                                <div className="w-1.5 h-1.5 rounded-full bg-green-500/50 animate-pulse" />
                                                                                <p className="text-[9px] text-white/30 font-medium">
                                                                                    Visual + Spatial Context Integrated
                                                                                </p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>

                                                    </div>
                                                );
                                            })()}

                                            {/* Completion info */}
                                            {selectedRequest.status === 'closed' && (selectedRequest.completion_message || selectedRequest.completion_photo_url) && (
                                                <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 mb-4">
                                                    <p className="text-green-400 font-medium text-sm mb-1">✓ {selectedRequest.closed_substatus === 'resolved' ? 'Resolved' : selectedRequest.closed_substatus === 'no_action' ? 'No Action Needed' : 'Referred'}</p>
                                                    {selectedRequest.completion_message && (
                                                        <p className="text-white/70 text-sm mb-2">{selectedRequest.completion_message}</p>
                                                    )}
                                                    {selectedRequest.completion_photo_url && (
                                                        <img
                                                            src={selectedRequest.completion_photo_url}
                                                            alt="Completion photo"
                                                            className="rounded-lg max-h-64 object-contain cursor-pointer hover:opacity-90 transition-opacity"
                                                            onClick={() => selectedRequest.completion_photo_url && window.open(selectedRequest.completion_photo_url, '_blank')}
                                                        />
                                                    )}
                                                </div>
                                            )}

                                            {/* Reporter Info - Simple inline */}
                                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/60 pt-3 border-t border-white/10">
                                                {(selectedRequest.first_name || selectedRequest.last_name) && (
                                                    <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" /> {selectedRequest.first_name} {selectedRequest.last_name}</span>
                                                )}
                                                <a href={`mailto:${selectedRequest.email}`} className="flex items-center gap-1 hover:text-primary-400"><Mail className="w-3.5 h-3.5" /> {selectedRequest.email}</a>
                                                {selectedRequest.phone && (
                                                    <a href={`tel:${selectedRequest.phone}`} className="flex items-center gap-1 hover:text-primary-400"><Phone className="w-3.5 h-3.5" /> {selectedRequest.phone}</a>
                                                )}
                                            </div>
                                        </div>

                                        {/* Merged ticket banner */}
                                    {selectedRequest.merged_into_id && (
                                        <div className="mx-3 sm:mx-4 mt-3 flex items-center gap-3 px-4 py-3 rounded-xl bg-purple-700/40 border border-purple-400/60">
                                            <GitMerge className="w-4 h-4 text-white flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-white">This ticket was merged</p>
                                                {selectedRequest.merge_comment && (
                                                    <p className="text-xs text-white/90 mt-0.5 truncate">{selectedRequest.merge_comment}</p>
                                                )}
                                                {selectedRequest.merged_by && (
                                                    <p className="text-xs text-white/75 mt-0.5">By {selectedRequest.merged_by}{selectedRequest.merged_at ? ` · ${new Date(selectedRequest.merged_at).toLocaleDateString()}` : ''}</p>
                                                )}
                                            </div>
                                            <button
                                                onClick={handleUnmerge}
                                                disabled={isUnmerging}
                                                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 border border-white/30 text-white text-xs font-semibold transition-colors disabled:opacity-50"
                                            >
                                                {isUnmerging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitMerge className="w-3.5 h-3.5" />}
                                                Un-merge
                                            </button>
                                        </div>
                                    )}
                                        {
                                            (selectedRequest.address || selectedRequest.lat) && (
                                                <div className="p-4 rounded-lg bg-slate-800/50 border border-white/10">
                                                    <div className="flex items-center gap-2 mb-3">
                                                        <MapPin className="w-4 h-4 text-blue-400" />
                                                        <span className="font-medium text-white">Location</span>
                                                    </div>
                                                    {selectedRequest.address && (
                                                        <p className="text-white/80 mb-3">{selectedRequest.address}</p>
                                                    )}
                                                    {/* Interactive map with Asset Overlay */}
                                                    {selectedRequest.lat && selectedRequest.long && (
                                                        <>
                                                            <div className="rounded-lg overflow-hidden h-64 bg-slate-900">
                                                                <RequestDetailMap
                                                                    lat={selectedRequest.lat}
                                                                    lng={selectedRequest.long}
                                                                    matchedAsset={(selectedRequest as any).matched_asset}
                                                                    mapLayers={mapLayers}
                                                                />
                                                            </div>
                                                            <a
                                                                href={`https://www.google.com/maps?q=${selectedRequest.lat},${selectedRequest.long}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="mt-2 flex items-center gap-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                                                            >
                                                                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                                                                Open in Google Maps ({selectedRequest.lat.toFixed(6)}, {selectedRequest.long.toFixed(6)})
                                                            </a>
                                                        </>
                                                    )}

                                                    {/* Matched Asset Info - Below Map */}
                                                    {(selectedRequest as any).matched_asset && (
                                                        <div className="mt-3 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                                                            {/* Clear header label */}
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500/70">🔗 Matched Asset</span>
                                                                {(selectedRequest as any).matched_asset.distance_meters && (
                                                                    <span className="text-xs text-white/40 ml-auto">
                                                                        {(selectedRequest as any).matched_asset.distance_meters < 1
                                                                            ? '<1m away'
                                                                            : `${Math.round((selectedRequest as any).matched_asset.distance_meters)}m away`}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <div className="w-3 h-3 rounded bg-emerald-500" />
                                                                <span className="text-sm font-medium text-emerald-400">
                                                                    {(selectedRequest as any).matched_asset.layer_name}
                                                                </span>
                                                            </div>
                                                            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                                                                {(selectedRequest as any).matched_asset.asset_id && (
                                                                    <>
                                                                        <span className="text-white/40">Asset ID</span>
                                                                        <span className="text-white/80 font-mono">{(selectedRequest as any).matched_asset.asset_id}</span>
                                                                    </>
                                                                )}
                                                                {(selectedRequest as any).matched_asset.asset_type && (
                                                                    <>
                                                                        <span className="text-white/40">Type</span>
                                                                        <span className="text-white/80">{(selectedRequest as any).matched_asset.asset_type}</span>
                                                                    </>
                                                                )}
                                                                {(selectedRequest as any).matched_asset.properties &&
                                                                    Object.entries((selectedRequest as any).matched_asset.properties)
                                                                        .filter(([key, value]) => {
                                                                            // Exclude common ID fields
                                                                            if (['id', 'asset_id', 'name', 'layer_name', 'objectid', 'fid', 'gid'].includes(key.toLowerCase())) return false;
                                                                            // Exclude purely numeric values (likely IDs)
                                                                            if (typeof value === 'number' && String(value).match(/^\d+$/)) return false;
                                                                            // Exclude null/undefined/empty
                                                                            if (value === null || value === undefined || value === '') return false;
                                                                            return true;
                                                                        })
                                                                        .slice(0, 6)
                                                                        .map(([key, value]) => (
                                                                            <React.Fragment key={key}>
                                                                                <span className="text-white/40 truncate">
                                                                                    {key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim()}
                                                                                </span>
                                                                                <span className="text-white/80 truncate">
                                                                                    {String(value)}
                                                                                </span>
                                                                            </React.Fragment>
                                                                        ))
                                                                }
                                                            </div>

                                                            {/* Asset History - Related Reports */}
                                                            <div className="mt-3 pt-3 border-t border-emerald-500/20">
                                                                <div className="flex items-center gap-2 mb-2">
                                                                    <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-500/70">📋 Asset History</span>
                                                                    {isLoadingAssetHistory && (
                                                                        <div className="w-3 h-3 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                                                                    )}
                                                                </div>
                                                                {assetRelatedRequests.length > 0 ? (
                                                                    <div className="space-y-2">
                                                                        <p className="text-xs text-emerald-400/80">
                                                                            {assetRelatedRequests.length} other report{assetRelatedRequests.length !== 1 ? 's' : ''} linked to this asset
                                                                        </p>
                                                                        <div className="space-y-1.5 max-h-32 overflow-y-auto">
                                                                            {assetRelatedRequests.slice(0, 5).map((r) => (
                                                                                <button
                                                                                    key={r.service_request_id}
                                                                                    onClick={() => loadRequestDetail(r.service_request_id)}
                                                                                    className="w-full text-left p-2 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors group"
                                                                                >
                                                                                    <div className="flex items-center justify-between">
                                                                                        <span className="text-xs font-mono text-emerald-400 group-hover:text-emerald-300">{r.service_request_id}</span>
                                                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${r.status === 'closed' ? 'bg-green-500/20 text-green-400' :
                                                                                            r.status === 'in_progress' ? 'bg-amber-500/20 text-amber-400' :
                                                                                                'bg-red-500/20 text-red-400'
                                                                                            }`}>{r.status.replace('_', ' ')}</span>
                                                                                    </div>
                                                                                    <p className="text-xs text-white/60 truncate mt-0.5">{r.service_name}</p>
                                                                                    <p className="text-[10px] text-white/40 mt-0.5">
                                                                                        {new Date(r.requested_datetime).toLocaleDateString()}
                                                                                    </p>
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                        {assetRelatedRequests.length > 5 && (
                                                                            <p className="text-[10px] text-white/40 text-center">
                                                                                +{assetRelatedRequests.length - 5} more reports
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                ) : !isLoadingAssetHistory ? (
                                                                    <p className="text-xs text-white/40">
                                                                        No previous reports for this asset
                                                                    </p>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        }


                                        {/* ═══ SECTION 4: Timeline ═══ */}
                                        <div className="p-4 rounded-lg bg-slate-800/50 border border-white/10">
                                            <div className="flex items-center gap-2 mb-4">
                                                <Clock className="w-4 h-4 text-blue-400" />
                                                <span className="font-medium text-white">Timeline</span>
                                                {auditLog.length > 0 && (
                                                    <span className="px-2 py-0.5 rounded-full text-xs bg-white/10 text-white/60">{auditLog.length} events</span>
                                                )}
                                            </div>

                                            {/* Timeline */}
                                            <div className="relative">
                                                <div className="space-y-3">
                                                    {/* Always show submitted event first, even if not in audit log */}
                                                    {(() => {
                                                        const hasSubmittedEvent = auditLog.some(e => e.action === 'submitted');
                                                        const timelineEntries = hasSubmittedEvent ? auditLog : [
                                                            {
                                                                id: -1,
                                                                service_request_id: 0,
                                                                action: 'submitted' as const,
                                                                new_value: 'open',
                                                                old_value: null,
                                                                actor_type: 'resident' as const,
                                                                actor_name: 'Resident',
                                                                created_at: selectedRequest.requested_datetime,
                                                                extra_data: null
                                                            },
                                                            ...auditLog
                                                        ];

                                                        return timelineEntries.map((entry, idx) => {
                                                            // Determine color and text based on action - simple circles, no emojis
                                                            let actionConfig: { color: string; text: string };

                                                            if (entry.action === 'submitted') {
                                                                actionConfig = { color: 'bg-emerald-500', text: 'Request submitted' };
                                                            } else if (entry.action === 'status_change') {
                                                                // Show both old and new status for clarity
                                                                const oldStatus = entry.old_value || 'unknown';
                                                                const newStatus = entry.new_value || 'unknown';
                                                                let statusText = '';

                                                                if (newStatus === 'closed') {
                                                                    const substatus = entry.extra_data?.substatus;
                                                                    statusText = `Closed ${substatus === 'resolved' ? '- Resolved' : substatus === 'no_action' ? '- No Action Needed' : substatus === 'third_party' ? '- Third Party' : ''}`;
                                                                } else if (newStatus === 'in_progress') {
                                                                    statusText = oldStatus === 'closed' ? 'Reopened as In Progress' : 'Marked as In Progress';
                                                                } else if (newStatus === 'open') {
                                                                    statusText = oldStatus === 'closed' ? 'Reopened' : oldStatus === 'in_progress' ? 'Reverted to Open' : 'Status set to Open';
                                                                } else {
                                                                    statusText = `Status: ${oldStatus} → ${newStatus}`;
                                                                }

                                                                actionConfig = {
                                                                    color: newStatus === 'closed' ? 'bg-emerald-500' : newStatus === 'in_progress' ? 'bg-blue-500' : 'bg-purple-500',
                                                                    text: statusText
                                                                };
                                                            } else if (entry.action === 'department_assigned') {
                                                                actionConfig = { color: 'bg-purple-500', text: `Assigned to ${entry.new_value}` };
                                                            } else if (entry.action === 'staff_assigned') {
                                                                actionConfig = { color: 'bg-indigo-500', text: `Assigned to ${entry.new_value}` };
                                                            } else if (entry.action === 'comment_added') {
                                                                actionConfig = { color: 'bg-teal-500', text: 'Comment added' };
                                                            } else if (entry.action === 'legal_hold') {
                                                                // Show if legal hold was enabled or removed
                                                                const isEnabled = entry.new_value === 'enabled';
                                                                actionConfig = {
                                                                    color: isEnabled ? 'bg-amber-500' : 'bg-gray-500',
                                                                    text: isEnabled ? '⚖️ Legal Hold Enabled' : '⚖️ Legal Hold Removed'
                                                                };
                                                            } else if (entry.action === 'visibility_change') {
                                                                const isPublic = entry.new_value === 'public';
                                                                actionConfig = {
                                                                    color: isPublic ? 'bg-emerald-500' : 'bg-slate-500',
                                                                    text: isPublic ? '🌐 Made Visible to Public' : '🔒 Hidden from Public'
                                                                };
                                                            } else if (entry.action === 'deleted') {
                                                                actionConfig = {
                                                                    color: 'bg-red-500',
                                                                    text: `🗑️ Soft Deleted: ${entry.new_value || 'No reason given'}`
                                                                };
                                                            } else if (entry.action === 'restored') {
                                                                actionConfig = {
                                                                    color: 'bg-green-500',
                                                                    text: '♻️ Request Restored'
                                                                };
                                                            } else if (entry.action === 'priority_accepted') {
                                                                actionConfig = {
                                                                    color: 'bg-emerald-500',
                                                                    text: `🤖 AI Priority Accepted: ${entry.new_value || ''}`
                                                                };
                                                            } else {
                                                                actionConfig = { color: 'bg-gray-500', text: entry.action };
                                                            }

                                                            const isLast = idx === auditLog.length - 1;

                                                            return (
                                                                <div key={entry.id} className="relative flex items-start gap-3 pl-0">
                                                                    {/* Simple circle indicator - centered on line */}
                                                                    <div className={`w-3.5 h-3.5 rounded-full ${actionConfig.color} shadow-sm ${isLast ? 'ring-2 ring-white/30' : ''}`} />

                                                                    {/* Content */}
                                                                    <div className="flex-1 min-w-0 -mt-0.5">
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            <span className="text-white/90 text-sm font-medium">{actionConfig.text}</span>
                                                                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${entry.actor_type === 'staff' || entry.actor_type === 'admin' ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'}`}>
                                                                                {entry.actor_type === 'staff' || entry.actor_type === 'admin' ? entry.actor_name || 'Staff' : 'Resident'}
                                                                            </span>
                                                                        </div>
                                                                        <div className="text-white/40 text-xs mt-0.5">
                                                                            {entry.created_at ? new Date(entry.created_at).toLocaleString() : 'No timestamp'}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        });
                                                    })()}
                                                </div>
                                            </div>
                                        </div>

                                        {/* ═══ SECTION 5: Comments ═══ */}
                                        <div className="p-4 rounded-lg bg-slate-800/50 border border-white/10">
                                            <div className="flex items-center gap-2 mb-4">
                                                <MessageSquare className="w-4 h-4 text-blue-400" />
                                                <span className="font-medium text-white">Comments</span>
                                                {comments.length > 0 && (
                                                    <span className="px-2 py-0.5 rounded-full text-xs bg-white/10 text-white/60">{comments.length}</span>
                                                )}
                                            </div>

                                            {/* Comments List */}
                                            {comments.length > 0 && (
                                                <div className="space-y-3 max-h-64 overflow-y-auto mb-4 pr-1">
                                                    {comments.map(c => (
                                                        <div key={c.id} className={`rounded-lg overflow-hidden ${c.visibility === 'internal' ? 'bg-orange-500/5' : 'bg-slate-700/30'}`}>
                                                            {/* Comment Header */}
                                                            <div className={`px-3 py-2 flex items-center gap-2 text-xs ${c.visibility === 'internal' ? 'bg-orange-500/10 border-b border-orange-500/20' : 'bg-slate-600/30 border-b border-white/5'}`}>
                                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${c.visibility === 'internal' ? 'bg-orange-500/20 text-orange-400' : 'bg-blue-500/20 text-blue-400'}`}>
                                                                    {c.username?.charAt(0).toUpperCase() || '?'}
                                                                </div>
                                                                <span className="font-medium text-white/90">{c.username}</span>
                                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${c.visibility === 'internal' ? 'bg-orange-500/20 text-orange-400' : 'bg-green-500/20 text-green-400'}`}>
                                                                    {c.visibility === 'internal' ? 'Internal' : 'Public'}
                                                                </span>
                                                                <span className="text-white/30 ml-auto">{c.created_at ? new Date(c.created_at).toLocaleString() : ''}</span>
                                                            </div>
                                                            {/* Comment Body */}
                                                            <div className="px-3 py-2.5">
                                                                <p className="text-sm text-white/80 leading-relaxed">{c.content}</p>
                                                                {c.photo_url && (
                                                                    <a href={c.photo_url} target="_blank" rel="noopener noreferrer" className="mt-2 block">
                                                                        <img src={c.photo_url} alt="attached" className="max-h-48 rounded-lg border border-white/10 object-cover cursor-pointer hover:opacity-90 transition-opacity" />
                                                                    </a>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {comments.length === 0 && (
                                                <div className="text-center py-6 text-white/30 text-sm mb-4">
                                                    No comments yet
                                                </div>
                                            )}

                                            {/* Add Comment */}
                                            <div className={`rounded-lg overflow-hidden ${commentVisibility === 'internal' ? 'bg-orange-950/30 border border-orange-500/30' : 'bg-green-950/30 border border-green-500/30'}`}>
                                                <div className={`px-3 py-2 flex items-center gap-2 border-b ${commentVisibility === 'internal' ? 'border-orange-500/20 bg-orange-500/10' : 'border-green-500/20 bg-green-500/10'}`}>
                                                    <span className={`text-xs font-semibold ${commentVisibility === 'internal' ? 'text-orange-300' : 'text-green-300'}`}>
                                                        {commentVisibility === 'internal' ? '🔒 Internal Note – Staff Only' : '🌐 Public Reply – Visible to Reporter'}
                                                    </span>
                                                    <button
                                                        onClick={() => setCommentVisibility(commentVisibility === 'internal' ? 'external' : 'internal')}
                                                        className={`ml-auto px-2.5 py-1 rounded text-xs font-semibold transition-all border ${commentVisibility === 'internal' ? 'bg-green-500/25 text-green-300 border-green-500/40 hover:bg-green-500/40' : 'bg-orange-500/25 text-orange-300 border-orange-500/40 hover:bg-orange-500/40'}`}
                                                    >
                                                        Switch to {commentVisibility === 'internal' ? 'Public' : 'Internal'}
                                                    </button>
                                                </div>
                                                {/* Photo preview */}
                                                {commentPhotoUrl && (
                                                    <div className="px-3 pt-2 flex items-start gap-2">
                                                        <img src={commentPhotoUrl} alt="preview" className="h-20 rounded-lg border border-white/10 object-cover" />
                                                        <button
                                                            onClick={() => setCommentPhotoUrl(null)}
                                                            className="text-white/40 hover:text-red-400 text-xs mt-1 transition-colors"
                                                            title="Remove photo"
                                                        >✕</button>
                                                    </div>
                                                )}
                                                <div className="p-3 flex gap-2">
                                                    <input
                                                        type="text"
                                                        placeholder={commentVisibility === 'internal' ? 'Add internal note...' : 'Reply to reporter...'}
                                                        aria-label={commentVisibility === 'internal' ? 'Add internal note' : 'Reply to reporter'}
                                                        value={newComment}
                                                        onChange={(e) => setNewComment(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddComment()}
                                                        className="flex-1 py-2 px-3 rounded-lg bg-slate-900/50 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    />
                                                    {/* Attach photo button */}
                                                    <input
                                                        ref={commentFileInputRef}
                                                        type="file"
                                                        accept="image/*"
                                                        className="hidden"
                                                        onChange={handleCommentPhotoSelect}
                                                    />
                                                    <button
                                                        onClick={() => commentFileInputRef.current?.click()}
                                                        disabled={commentPhotoUploading}
                                                        className="px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white/60 hover:text-white disabled:opacity-50 transition-colors flex items-center gap-1.5"
                                                        title="Attach photo"
                                                        aria-label="Attach photo"
                                                    >
                                                        {commentPhotoUploading
                                                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                                                            : <Paperclip className="w-4 h-4" />
                                                        }
                                                    </button>
                                                    <button
                                                        onClick={handleAddComment}
                                                        disabled={(!newComment.trim() && !commentPhotoUrl) || isSubmittingComment}
                                                        className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50 flex items-center gap-2 transition-colors"
                                                        aria-label="Send comment"
                                                    >
                                                        <Send className="w-4 h-4" aria-hidden="true" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* ═══ Actions Footer ═══ */}
                                        <div className="p-4 rounded-lg bg-slate-800/50 border border-white/10">
                                            <div className="flex gap-3">
                                                {/* Share Link Dropdown */}
                                                <div className="relative flex-1">
                                                    <button
                                                        onClick={() => setShowShareMenu(!showShareMenu)}
                                                        className="w-full py-2.5 px-4 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                                                    >
                                                        <Link className="w-4 h-4" />
                                                        Share Link
                                                        <ChevronDown className={`w-4 h-4 transition-transform ${showShareMenu ? 'rotate-180' : ''}`} />
                                                    </button>

                                                    {showShareMenu && (
                                                        <div className="absolute bottom-full left-0 right-0 mb-2 bg-slate-800 rounded-lg border border-white/10 shadow-xl overflow-hidden z-20">
                                                            <button
                                                                onClick={() => {
                                                                    // Use current status for staff link (active, in_progress, resolved)
                                                                    const statusPath = selectedRequest.status === 'open' ? 'active' :
                                                                        selectedRequest.status === 'in_progress' ? 'in_progress' : 'resolved';
                                                                    navigator.clipboard.writeText(`${window.location.origin}/staff#${statusPath}/request/${selectedRequest.service_request_id}`);
                                                                    setCopiedLink('staff');
                                                                    setTimeout(() => { setCopiedLink(null); setShowShareMenu(false); }, 1500);
                                                                }}
                                                                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors text-left"
                                                            >
                                                                <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                                                                    {copiedLink === 'staff' ? <Check className="w-4 h-4 text-green-400" /> : <Link className="w-4 h-4 text-purple-400" />}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-sm font-medium text-white">
                                                                        {copiedLink === 'staff' ? 'Copied!' : 'Staff Portal Link'}
                                                                    </div>
                                                                    <div className="text-xs text-white/70 truncate">For internal staff use</div>
                                                                </div>
                                                            </button>
                                                            <div className="border-t border-white/5" />
                                                            <button
                                                                onClick={() => {
                                                                    // Resident portal uses /#track/ID format
                                                                    navigator.clipboard.writeText(`${window.location.origin}/#track/${selectedRequest.service_request_id}`);
                                                                    setCopiedLink('resident');
                                                                    setTimeout(() => { setCopiedLink(null); setShowShareMenu(false); }, 1500);
                                                                }}
                                                                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors text-left"
                                                            >
                                                                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                                                    {copiedLink === 'resident' ? <Check className="w-4 h-4 text-green-400" /> : <ExternalLink className="w-4 h-4 text-blue-400" />}
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="text-sm font-medium text-white">
                                                                        {copiedLink === 'resident' ? 'Copied!' : 'Resident Portal Link'}
                                                                    </div>
                                                                    <div className="text-xs text-white/70 truncate">Share with the reporter</div>
                                                                </div>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                {(user?.role === 'admin' || user?.role === 'staff_admin' || user?.role === 'org_admin') && (
                                                    <button onClick={() => setShowDeleteModal(true)} className="py-2.5 px-4 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 text-sm font-medium flex items-center gap-2 transition-colors">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => { setShowMergeModal(true); setMergeError(null); setMergeSelectedIds([]); setMergeComment(''); setMergeSearchQuery(''); }}
                                                    className="py-2.5 px-4 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 text-purple-400 text-sm font-medium flex items-center gap-2 transition-colors"
                                                    title="Merge tickets"
                                                >
                                                    <GitMerge className="w-4 h-4" />
                                                    <span>Merge</span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-white/40">
                                    <div className="text-center">
                                        <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                        <p className="text-sm">Select an incident</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )
                }
            </div>

            {/* Close Request Modal - Substatus Selection */}
            < Modal isOpen={showClosedModal} onClose={() => setShowClosedModal(false)} title="Close Request" >
                <div className="space-y-6">
                    <p className="text-white/70 text-sm">Select a resolution type for this request:</p>

                    <div className="space-y-3">
                        <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${closedSubstatus === 'no_action' ? 'bg-orange-500/10 border-orange-500/30' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                            <input
                                type="radio"
                                name="closedSubstatus"
                                value="no_action"
                                checked={closedSubstatus === 'no_action'}
                                onChange={() => setClosedSubstatus('no_action')}
                                className="mt-1"
                            />
                            <div>
                                <p className="font-medium text-white">No Action Needed</p>
                                <p className="text-sm text-white/50">Issue doesn't require municipal intervention</p>
                            </div>
                        </label>

                        <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${closedSubstatus === 'resolved' ? 'bg-green-500/10 border-green-500/30' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                            <input
                                type="radio"
                                name="closedSubstatus"
                                value="resolved"
                                checked={closedSubstatus === 'resolved'}
                                onChange={() => setClosedSubstatus('resolved')}
                                className="mt-1"
                            />
                            <div>
                                <p className="font-medium text-white">Resolved</p>
                                <p className="text-sm text-white/50">Issue has been fixed by municipal staff</p>
                            </div>
                        </label>

                        <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${closedSubstatus === 'third_party' ? 'bg-blue-500/10 border-blue-500/30' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}>
                            <input
                                type="radio"
                                name="closedSubstatus"
                                value="third_party"
                                checked={closedSubstatus === 'third_party'}
                                onChange={() => setClosedSubstatus('third_party')}
                                className="mt-1"
                            />
                            <div>
                                <p className="font-medium text-white">Third Party Contacted</p>
                                <p className="text-sm text-white/50">Referred to utility company, state agency, etc.</p>
                            </div>
                        </label>
                    </div>

                    <Textarea
                        label="Completion Message (optional)"
                        placeholder="Add a message about the resolution..."
                        value={completionMessage}
                        onChange={(e) => setCompletionMessage(e.target.value)}
                    />

                    {closedSubstatus === 'resolved' && (
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-white/70">Completion Photo (optional)</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="file"
                                    accept="image/*"
                                    id="completion-photo-upload"
                                    className="hidden"
                                    onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            try {
                                                const result = await api.uploadImage(file);
                                                setCompletionPhotoUrl(result.url);
                                            } catch (err) {
                                                console.error('Upload failed:', err);
                                                alert("Failed to upload image");
                                            }
                                        }
                                    }}
                                />
                                <label
                                    htmlFor="completion-photo-upload"
                                    className="px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm cursor-pointer hover:bg-white/20 transition-colors flex items-center gap-2"
                                >
                                    <Camera className="w-4 h-4" />
                                    {completionPhotoUrl ? 'Change Photo' : 'Upload Photo'}
                                </label>
                                {completionPhotoUrl && (
                                    <div className="flex items-center gap-2">
                                        <img src={completionPhotoUrl} alt="Completion" className="h-12 w-12 object-cover rounded-lg" />
                                        <button
                                            onClick={() => setCompletionPhotoUrl('')}
                                            className="text-red-400 hover:text-red-300 text-sm"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="ghost" onClick={() => setShowClosedModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCloseWithSubstatus}>
                            Close Request
                        </Button>
                    </div>
                </div>
            </Modal >

            {/* Delete Request Modal */}
            < Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Delete Request" >
                <div className="space-y-4">
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                        <p className="text-red-400 font-medium flex items-center gap-2">
                            <Trash2 className="w-5 h-5" />
                            This will soft-delete the request
                        </p>
                        <p className="text-white/60 text-sm mt-2">
                            The request will be hidden from the normal view but will remain accessible to administrators.
                        </p>
                    </div>

                    <Textarea
                        label="Justification *"
                        placeholder="Explain why this request should be deleted (minimum 10 characters)..."
                        value={deleteJustification}
                        onChange={(e) => setDeleteJustification(e.target.value)}
                        required
                    />

                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDeleteRequest}
                            disabled={deleteJustification.length < 10 || isDeleting}
                        >
                            {isDeleting ? 'Deleting...' : 'Delete Request'}
                        </Button>
                    </div>
                </div>
            </Modal >

            {/* Merge Tickets Modal */}
            <Modal isOpen={showMergeModal} onClose={() => setShowMergeModal(false)} title="Merge Tickets">
                {selectedRequest && (
                    <div className="space-y-4">
                        <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/20 text-sm text-purple-300">
                            <strong>Target (keep):</strong> #{selectedRequest.service_request_id} — {selectedRequest.service_name}
                        </div>
                        <p className="text-white/60 text-sm">
                            Select one or more tickets below to merge <em>into</em> this one. The selected tickets will be closed and linked here. Reporters will be notified via SMS and Telegram.
                        </p>

                        {/* Search */}
                        <input
                            type="text"
                            value={mergeSearchQuery}
                            onChange={e => setMergeSearchQuery(e.target.value)}
                            placeholder="Search by ID, description or address…"
                            className="w-full px-3 py-2 rounded-lg bg-slate-900/50 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />

                        {/* Ticket list */}
                        <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1">
                            {requests
                                .filter(r =>
                                    r.service_request_id !== selectedRequest.service_request_id &&
                                    !r.merged_into_id &&
                                    (!mergeSearchQuery.trim() ||
                                        r.service_request_id.toLowerCase().includes(mergeSearchQuery.toLowerCase()) ||
                                        (r.description || '').toLowerCase().includes(mergeSearchQuery.toLowerCase()) ||
                                        (r.address || '').toLowerCase().includes(mergeSearchQuery.toLowerCase())
                                    )
                                )
                                .map(r => {
                                    const selected = mergeSelectedIds.includes(r.service_request_id);
                                    return (
                                        <button
                                            key={r.service_request_id}
                                            onClick={() => setMergeSelectedIds(prev =>
                                                selected ? prev.filter(id => id !== r.service_request_id) : [...prev, r.service_request_id]
                                            )}
                                            className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${selected ? 'bg-purple-500/20 border-purple-500/50 text-white' : 'bg-slate-800/50 border-white/10 text-white/70 hover:bg-slate-700/50'}`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${selected ? 'bg-purple-500 border-purple-400' : 'border-white/30'}`}>
                                                    {selected && <Check className="w-3 h-3 text-white" />}
                                                </div>
                                                <span className="font-mono text-xs text-white/50">{r.service_request_id.slice(-10)}</span>
                                                <span className="font-medium truncate">{r.service_name}</span>
                                                {r.address && <span className="text-white/40 truncate text-xs ml-auto">{r.address}</span>}
                                            </div>
                                        </button>
                                    );
                                })
                            }
                            {requests.filter(r => r.service_request_id !== selectedRequest.service_request_id && !r.merged_into_id).length === 0 && (
                                <p className="text-white/30 text-sm text-center py-4">No other open tickets to merge</p>
                            )}
                        </div>

                        {mergeSelectedIds.length > 0 && (
                            <p className="text-xs text-purple-300">{mergeSelectedIds.length} ticket(s) selected to merge in</p>
                        )}

                        {/* Comment */}
                        <div>
                            <label className="block text-xs text-white/50 mb-1">Comment (optional — posted to all tickets)</label>
                            <input
                                type="text"
                                value={mergeComment}
                                onChange={e => setMergeComment(e.target.value)}
                                placeholder="Reason for merging…"
                                className="w-full px-3 py-2 rounded-lg bg-slate-900/50 border border-white/10 text-white text-sm placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-purple-500"
                            />
                        </div>

                        {mergeError && (
                            <p className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">{mergeError}</p>
                        )}

                        <div className="flex justify-end gap-3 pt-2">
                            <Button variant="ghost" onClick={() => setShowMergeModal(false)}>Cancel</Button>
                            <Button
                                onClick={handleMergeTickets}
                                disabled={mergeSelectedIds.length === 0 || isMerging}
                            >
                                {isMerging ? 'Merging…' : `Merge ${mergeSelectedIds.length} ticket(s)`}
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>

            {/* Premium Photo Lightbox Modal */}
            {
                lightboxUrl && (
                    <div
                        className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
                        onClick={() => setLightboxUrl(null)}
                    >
                        {/* Backdrop with blur */}
                        <div className="absolute inset-0 bg-black/95 backdrop-blur-xl" />

                        {/* Close button */}
                        <button
                            onClick={() => setLightboxUrl(null)}
                            className="absolute top-4 right-4 md:top-6 md:right-6 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 transition-all duration-300 group"
                            aria-label="Close image preview"
                        >
                            <X className="w-6 h-6 text-white group-hover:rotate-90 transition-transform duration-300" aria-hidden="true" />
                        </button>

                        {/* Image container with premium styling */}
                        <div
                            className="relative z-10 max-w-[90vw] max-h-[85vh] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Gradient glow effect behind image */}
                            <div className="absolute -inset-1 bg-gradient-to-r from-primary-500/30 via-purple-500/30 to-primary-500/30 blur-xl opacity-50" />

                            {/* Image */}
                            <img
                                src={lightboxUrl}
                                alt="Full size preview"
                                className="relative max-w-full max-h-[85vh] object-contain bg-gray-900/50 rounded-2xl"
                            />
                        </div>

                        {/* Instructions */}
                        <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm">
                            Click anywhere to close
                        </p>
                    </div>
                )
            }
            {/* Notification Settings Modal */}
            <NotificationSettings
                isOpen={showNotificationSettings}
                onClose={() => setShowNotificationSettings(false)}
                userName={user?.full_name || user?.username || 'User'}
            />
            {/* Activity Feed Panel */}
            <ActivityFeed
                isOpen={showActivityFeed}
                onClose={() => setShowActivityFeed(false)}
                requests={allRequests}
                userId={user?.username || ''}
                userDepartmentIds={userDepartmentIds}
                onSelectRequest={(request) => {
                    // Find and select this request by its ID
                    handleMapRequestSelect(request.service_request_id);
                    setSidebarOpen(false);
                }}
            />
        </div >
    );
}
